import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api } from '../api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import ProductSearchDropdown from '../components/ProductSearchDropdown';
import DateRangeCalendar from '../components/DateRangeCalendar';
import { todayMSK, todayMSKString } from '../utils/timezone';
import HeaderSelect from '../components/HeaderSelect';
import DatePickerCalendar from '../components/DatePickerCalendar';
// PackagingPage перенесён в Каталог
import Tooltip from '../components/Tooltip';
import Select from '../components/Select';
import OrderModal from '../components/OrderModal';
import { TrashIcon } from '../components/ActionIcons';
import { usePagedList, LoadMoreButton } from '../hooks/usePagedList';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

function getLocalToday() {
  // Жёстко московское время — независимо от таймзоны браузера админа.
  // en-CA формат "YYYY-MM-DD" подходит для бэкенд-полей (StockInvoice.date и т.п.).
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

const TAB_INVOICES = 'invoices';
const TAB_WRITEOFFS = 'writeoffs';
const TAB_MISGRADES = 'misgrades';
// Кнопки создания в строке вкладок — как «+ Добавить» в Каталоге: главная сплошная, вторая контурная (UX-аудит 25.10)
const TAB_ACTION_STYLE = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', '--ch': '8px', fontSize: 14, fontWeight: 600, lineHeight: 1.1 };
const TAB_INVENTORY = 'inventory';
const TAB_BOUQUET_INVENTORY = 'bouquet_inventory';
const TAB_MOVEMENTS = 'movements';
// TAB_PACKAGING перенесён в Каталог (ProductsPage)

const statusLabel = {
  draft: 'Черновик',
  accepted: 'Принята',
  planned: 'Ожидает',
};
const statusBadge = {
  draft: 'badge-new',
  accepted: 'badge-active',
  planned: 'badge-new',
};

const movementTypeLabel = {
  receipt: 'Приход',
  sale: 'Продажа',
  subscription_fill: 'Наполнение',
  write_off: 'Списание',
  transfer_out: 'Перемещение',
  defect_on_receipt: 'Брак при приёмке',
  cancellation_return: 'Отмена заказа',
  misgrade: 'Пересорт',
  inventory: 'Инвентаризация',
  completion: 'Завершение',
  edit_return: 'Редактирование (-)',
  edit_sale: 'Редактирование (+)',
  reserve_planned: 'Резерв из плановой',
  reserve_to_physical: 'Перенос план→склад',
  reserve_transfer_planned: 'Перенос на плановую',
  reserve_back_to_current: 'Возврат на склад',
  deficit_create: 'Заказ-долг',
  deficit_cover: 'Покрытие дефицита',
  reserve_release_planned: 'Снятие план-резерва',
  bouquet_assembly_reserve: 'Резерв под букет',
  bouquet_task_cancel: 'Отмена задания на сборку',
  bouquet_assembly_release: 'Возврат при разборке букета',
  bouquet_completion: 'Списание (букет в заказе)',
  bouquet_cancellation_return: 'Возврат букета (отмена заказа)',
  bouquet_disassembly_writeoff: 'Списание букета (порча/утиль)',
  bouquet_found: 'Найденный букет (оприходование)',
};
// Цветовая семантика в брендовой палитре:
//   primary green (#1C3D2E) — приход / возврат на склад / резерв из планов (положительное движение)
//   coral red (#B0603C) — продажа / списание / завершение / брак (товар уходит)
//   amber (#7E5D00) — дефицит / редактирование заказа (внимание)
//   gray (#5E6561) — учётные/служебные операции (инвентаризация, пересорт, освобождение резерва)
const movementTypeColor = {
  receipt: '#1C3D2E',
  deficit_cover: '#1C3D2E',
  cancellation_return: '#1C3D2E',
  reserve_back_to_current: '#1C3D2E',
  reserve_to_physical: '#1C3D2E',
  reserve_planned: '#1C3D2E',
  reserve_transfer_planned: '#1C3D2E',

  sale: '#B0603C',
  subscription_fill: '#B0603C',
  write_off: '#B0603C',
  transfer_out: '#B0603C',
  defect_on_receipt: '#B0603C',
  completion: '#B0603C',

  deficit_create: '#7E5D00',
  edit_sale: '#7E5D00',
  edit_return: '#7E5D00',

  misgrade: '#5E6561',
  inventory: '#5E6561',
  reserve_release_planned: '#5E6561',
  bouquet_assembly_reserve: '#7E5D00',
  bouquet_task_cancel: '#1C3D2E',
  bouquet_assembly_release: '#1C3D2E',
  bouquet_completion: '#B0603C',
  bouquet_cancellation_return: '#1C3D2E',
  bouquet_disassembly_writeoff: '#B0603C',
  bouquet_found: '#1C3D2E',
};

// Типы движений, которые НЕ меняют физ. остаток (только логические резервы).
// Колонка "Физ. остаток" для них рендерится приглушённо со скобками: (8) вместо 8.
const LOGICAL_MOVEMENT_TYPES = new Set([
  'reserve_planned', 'reserve_to_physical', 'reserve_transfer_planned',
  'reserve_back_to_current',
  'deficit_create', 'deficit_cover',
  'reserve_release_planned',
  'bouquet_assembly_reserve', 'bouquet_assembly_release', 'bouquet_task_cancel',
]);

const reasonLabel = {
  wilting: 'Увядание',
  damage: 'Повреждение',
  transfer: 'Перемещение',  // на точку из «Настройки → Точки перемещения»
  misgrade: 'Пересорт',  // legacy
};

// Причина строки акта: для перемещения — «→ название точки»
const writeOffReasonText = (item) => (item.reason === 'transfer' && item.transfer_point_name
  ? `→ ${item.transfer_point_name}`
  : (reasonLabel[item.reason] || item.reason));

// Тип списания акта для списка: причины его позиций без повторов
const writeOffTypes = (wo) => [...new Set((wo.items || []).map(writeOffReasonText))].join(', ');

// Стиль для inline-ссылок на накладную/заказ внутри текста комментария
const REF_LINK_STYLE = {
  color: '#1C3D2E',
  fontWeight: 600,
  cursor: 'pointer',
  borderBottom: '1px dashed #1C3D2E',
  textDecoration: 'none',
};

// Экранируем строку для использования в RegExp.
function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Находит в comment-е prefix относящийся к source-типу (write_off / inventory / misgrade).
// Возвращает substring для подсветки или null если не найден.
function findSourcePrefix(comment, sourceType) {
  if (!comment || !sourceType) return null;
  if (sourceType === 'write_off') {
    // "Списание №42 (Увядание)" — с номером (новый формат) или без (legacy)
    const m = comment.match(/^(Списание(?:\s+№\d+)? \([^)]+\))/);
    return m ? m[1] : null;
  }
  if (sourceType === 'inventory') {
    // "Инвентаризация №5" или "Инвентаризация" — берём до пробел-em-dash или конца
    const m = comment.match(/^(Инвентаризация(?:\s+№\d+)?)(?:\s+—|\s*$)/);
    return m ? m[1] : null;
  }
  if (sourceType === 'misgrade') {
    // "Пересорт №7 в «X»" или "Пересорт в «X»" / "Пересорт ← Y" / "Пересорт → Y"
    const m = comment.match(/^(Пересорт[^—]*?)(?:\s+—|\s*$)/);
    return m ? m[1].trim() : null;
  }
  return null;
}

// Рендерит comment, заменяя в нём substring накладной (invoiceLabel),
// substring-и заказов (orders[i].label) и source-prefix (write_off/inventory/misgrade)
// кликабельными span'ами. Если ни refs нет — рендерит plain текст.
function CommentWithRefs({ comment, invoiceLabel, invoiceId, orders,
                          sourceType, sourceId,
                          onOpenInvoice, onOpenOrder,
                          onOpenWriteOff, onOpenInventory, onOpenMisgrade }) {
  if (!comment) return '—';
  const refs = [];
  if (invoiceLabel && invoiceId) {
    refs.push({ label: invoiceLabel, kind: 'invoice', id: invoiceId });
  }
  if (Array.isArray(orders)) {
    for (const o of orders) {
      if (o && o.label && o.id) refs.push({ label: o.label, kind: 'order', id: o.id });
    }
  }
  if (sourceType && sourceId) {
    const prefix = findSourcePrefix(comment, sourceType);
    if (prefix) {
      refs.push({ label: prefix, kind: sourceType, id: sourceId });
    }
  }
  if (!refs.length) return comment;

  // Находим все вхождения [start, end, ref] и сортируем по позиции.
  const matches = [];
  for (const r of refs) {
    const re = new RegExp(escapeRegExp(r.label), 'g');
    let m;
    while ((m = re.exec(comment)) !== null) {
      matches.push({ start: m.index, end: m.index + m[0].length, ref: r });
      if (m.index === re.lastIndex) re.lastIndex++;  // защита от zero-width
    }
  }
  if (!matches.length) return comment;
  matches.sort((a, b) => a.start - b.start);

  // Собираем массив фрагментов: текст между matches + кликабельные span-ы.
  const out = [];
  let cursor = 0;
  let key = 0;
  for (const m of matches) {
    if (m.start < cursor) continue; // overlapping match (orders.label может быть подстрокой) — пропускаем
    if (m.start > cursor) out.push(comment.slice(cursor, m.start));
    const handlers = {
      invoice: () => onOpenInvoice(m.ref.id),
      order: () => onOpenOrder(m.ref.id),
      write_off: () => onOpenWriteOff(m.ref.id),
      inventory: () => onOpenInventory(m.ref.id),
      misgrade: () => onOpenMisgrade(m.ref.id),
    };
    const titles = {
      invoice: 'Открыть накладную',
      order: 'Открыть заказ',
      write_off: 'Открыть списание',
      inventory: 'Открыть инвентаризацию',
      misgrade: 'Открыть пересорт',
    };
    out.push(
      <span
        key={`r${key++}`}
        onClick={handlers[m.ref.kind]}
        style={REF_LINK_STYLE}
        title={titles[m.ref.kind]}
      >
        {m.ref.label}
      </span>
    );
    cursor = m.end;
  }
  if (cursor < comment.length) out.push(comment.slice(cursor));
  return out;
}

// Иконки табов — line-style SVG (наследуют currentColor)
const TAB_ICONS = {
  invoices: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <line x1="8" y1="17" x2="16" y2="17"/>
    </svg>
  ),
  writeoffs: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
    </svg>
  ),
  misgrade: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9"/>
      <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
      <polyline points="7 23 3 19 7 15"/>
      <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
    </svg>
  ),
  inventory: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="18" rx="2"/>
      <line x1="9" y1="9" x2="15" y2="9"/>
      <line x1="9" y1="13" x2="15" y2="13"/>
      <line x1="9" y1="17" x2="13" y2="17"/>
    </svg>
  ),
  movements: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <polyline points="12 7 12 12 15 14"/>
    </svg>
  ),
  packaging: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 7H4l-2 5v9a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-9z"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 7v15"/>
      <path d="M8.5 7l1.5-4h4l1.5 4"/>
    </svg>
  ),
};

const PLUS_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

function WarehousePage({ showToast }) {
  // Вкладку может задать переход из статистики («Все списания — Склад → Списания»): метка разовая
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = sessionStorage.getItem('nf_open_tab_warehouse');
      if (t) { sessionStorage.removeItem('nf_open_tab_warehouse'); return t; }
    } catch (e) { /* no-op */ }
    return TAB_INVOICES;
  });
  // Триггер для InvoicesTab открыть форму создания (drafts/planned)
  const [invoiceCreateTrigger, setInvoiceCreateTrigger] = useState(null);
  const [writeOffCreateTrigger, setWriteOffCreateTrigger] = useState(false);
  const [misgradeCreateTrigger, setMisgradeCreateTrigger] = useState(false);
  const [inventoryCreateTrigger, setInventoryCreateTrigger] = useState(false);
  const [bouquetInvCreateTrigger, setBouquetInvCreateTrigger] = useState(false);

  const tabBtn = (id, label, icon) => (
    <button
      className={`tab${activeTab === id ? ' active' : ''}`}
      onClick={() => setActiveTab(id)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Склад</h1>
      </div>

      <div className="tabs" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        {tabBtn(TAB_INVOICES, 'Накладные', TAB_ICONS.invoices)}
        {tabBtn(TAB_WRITEOFFS, 'Списания', TAB_ICONS.writeoffs)}
        {tabBtn(TAB_MISGRADES, 'Пересорт', TAB_ICONS.misgrade)}
        {tabBtn(TAB_INVENTORY, 'Инвентаризация', TAB_ICONS.inventory)}
        {tabBtn(TAB_MOVEMENTS, 'История', TAB_ICONS.movements)}

        {/* Кнопки создания — справа, отображаются по активному табу */}
        {activeTab === TAB_INVOICES && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, marginBottom: 4 }}>
            <button
              className="btn btn-primary"
              onClick={() => setInvoiceCreateTrigger('draft')}
              style={TAB_ACTION_STYLE}
            >
              {PLUS_ICON}
              Приходная
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setInvoiceCreateTrigger('planned')}
              style={TAB_ACTION_STYLE}
            >
              {PLUS_ICON}
              Плановая
            </button>
          </div>
        )}
        {activeTab === TAB_WRITEOFFS && (
          <div style={{ marginLeft: 'auto', marginBottom: 4 }}>
            <button className="btn btn-primary" onClick={() => setWriteOffCreateTrigger(true)}
              style={TAB_ACTION_STYLE}>
              {PLUS_ICON} Списание
            </button>
          </div>
        )}
        {activeTab === TAB_MISGRADES && (
          <div style={{ marginLeft: 'auto', marginBottom: 4 }}>
            <button className="btn btn-primary" onClick={() => setMisgradeCreateTrigger(true)}
              style={TAB_ACTION_STYLE}>
              {PLUS_ICON} Пересорт
            </button>
          </div>
        )}
        {activeTab === TAB_INVENTORY && (
          <div style={{ marginLeft: 'auto', marginBottom: 4, display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => setInventoryCreateTrigger(true)}
              style={TAB_ACTION_STYLE}>
              {PLUS_ICON} Инвентаризация стеблей
            </button>
            <button className="btn btn-secondary" onClick={() => setBouquetInvCreateTrigger(true)}
              style={TAB_ACTION_STYLE}>
              {PLUS_ICON} Инвентаризация букетов
            </button>
          </div>
        )}
      </div>

      {activeTab === TAB_INVOICES && (
        <InvoicesTab
          showToast={showToast}
          createTrigger={invoiceCreateTrigger}
          onCreateHandled={() => setInvoiceCreateTrigger(null)}
        />
      )}
      {activeTab === TAB_WRITEOFFS && <WriteOffsTab showToast={showToast} createTrigger={writeOffCreateTrigger} onCreateHandled={() => setWriteOffCreateTrigger(false)} />}
      {activeTab === TAB_MISGRADES && <MisgradesTab showToast={showToast} createTrigger={misgradeCreateTrigger} onCreateHandled={() => setMisgradeCreateTrigger(false)} />}
      {activeTab === TAB_INVENTORY && (
        <InventoryRegistry showToast={showToast}
          stemsCreateTrigger={inventoryCreateTrigger} onStemsHandled={() => setInventoryCreateTrigger(false)}
          bouquetCreateTrigger={bouquetInvCreateTrigger} onBouquetHandled={() => setBouquetInvCreateTrigger(false)} />
      )}
      {activeTab === TAB_MOVEMENTS && <MovementsTab showToast={showToast} />}
    </div>
  );
}

// ============================================================
// Компонент выбора диапазона дат
// ============================================================
function DateRangeFilter({ dateFrom, dateTo, onChange }) {
  const [showCalendar, setShowCalendar] = useState(false);

  const displayText = dateFrom && dateTo
    ? `${formatDate(dateFrom)} — ${formatDate(dateTo)}`
    : dateFrom
    ? `с ${formatDate(dateFrom)}`
    : dateTo
    ? `до ${formatDate(dateTo)}`
    : 'Период';

  const hasValue = dateFrom || dateTo;

  return (
    <div style={{ position: 'relative' }}>
      <label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Период</label>
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          type="button"
          className="form-input"
          onClick={() => setShowCalendar(!showCalendar)}
          style={{
            padding: '6px 10px', fontSize: 13, cursor: 'pointer',
            textAlign: 'left', minWidth: 170, background: '#fff',
            color: hasValue ? '#333' : '#999',
          }}
        >
          {displayText}
        </button>
        {hasValue && (
          <button
            type="button"
            onClick={() => { onChange('', ''); }}
            style={{
              background: 'none', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd',
              cursor: 'pointer', fontSize: 14, color: '#999', padding: '0 6px',
            }}
            title="Сбросить"
          >
            ✕
          </button>
        )}
      </div>
      {showCalendar && (
        <DateRangeCalendar
          dateFrom={dateFrom}
          dateTo={dateTo}
          onSelect={(from, to) => {
            onChange(from, to);
            setShowCalendar(false);
          }}
          onClose={() => setShowCalendar(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// TAB: Накладные (Invoices)
// ============================================================
function InvoicesTab({ showToast, createTrigger, onCreateHandled }) {
  const [suppliers, setSuppliers] = useState([]);
  const [createInvoiceType, setCreateInvoiceType] = useState(null); // 'draft' | 'planned' | null
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingFromView, setEditingFromView] = useState(false);  // true → при закрытии edit вернуться в view
  const [viewInvoice, setViewInvoice] = useState(null);
  const [acceptingInvoice, setAcceptingInvoice] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: '', dateTo: '',
    arrivalFrom: '', arrivalTo: '',
    supplierId: [],   // multi-select (массив id)
    status: '', invoiceNumber: '', invoiceType: '',
  });

  // Список — страницами по 50 (usePagedList), все фильтры серверные: раньше номер, тип,
  // мультивыбор поставщика и дата поступления резались на клиенте по полному списку.
  const fetchPage = useCallback(({ limit, offset }) => api.getInvoices({
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    supplierIds: filters.supplierId,
    status: filters.status,
    invoiceNumber: filters.invoiceNumber,
    invoiceType: filters.invoiceType,
    arrivalFrom: filters.arrivalFrom,
    arrivalTo: filters.arrivalTo,
    limit, offset,
  }), [filters]);
  const onListError = useCallback(() => showToast('Ошибка загрузки накладных', 'error'), [showToast]);
  const { items: invoices, isLoading, hasMore, loadingMore, reload: loadData, loadMore } =
    usePagedList(fetchPage, { onError: onListError });

  useEffect(() => {
    api.getSuppliers().then(setSuppliers).catch(() => {});
  }, []);

  // Реагируем на триггер создания из шапки страницы
  useEffect(() => {
    if (createTrigger) {
      setCreateInvoiceType(createTrigger);
      onCreateHandled && onCreateHandled();
    }
  }, [createTrigger, onCreateHandled]);

  const handleAccept = (id) => {
    setConfirmDialog({
      title: 'Приёмка накладной',
      message: 'Принять накладную? Товары будут добавлены на склад.',
      confirmText: 'Принять',
      variant: 'success',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.acceptInvoice(id);
          showToast('Накладная принята, товары добавлены на склад');
          loadData();
        } catch (error) {
          showToast(error.message || 'Ошибка приёмки', 'error');
        }
      },
    });
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      title: 'Удаление накладной',
      message: 'Удалить черновик накладной?',
      confirmText: 'Удалить',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.deleteInvoice(id);
          showToast('Накладная удалена');
          loadData();
        } catch (error) {
          showToast(error.message || 'Ошибка удаления', 'error');
        }
      },
    });
  };

  const handleViewInvoice = async (id) => {
    try {
      const data = await api.getInvoice(id);
      setViewInvoice(data);
    } catch {
      showToast('Ошибка загрузки накладной', 'error');
    }
  };

  const handleEditInvoice = async (id) => {
    try {
      const data = await api.getInvoice(id);
      setEditingInvoice(data);
    } catch {
      showToast('Ошибка загрузки накладной', 'error');
    }
  };

  // Проверка, что сохранённая накладная попадает под активные фильтры (все они серверные —
  // повторяем их здесь, чтобы после сохранения честно сказать «не отображается из-за фильтров»).
  const matchesActiveFilters = useCallback((inv) => {
    if (!inv) return false;
    if (filters.dateFrom && inv.date && inv.date < filters.dateFrom) return false;
    if (filters.dateTo && inv.date && inv.date > filters.dateTo) return false;
    if (filters.supplierId.length > 0 && !filters.supplierId.map(String).includes(String(inv.supplier_id))) return false;
    if (filters.status && inv.status !== filters.status) return false;
    if (filters.invoiceNumber && !(inv.invoice_number || '').toLowerCase().includes(filters.invoiceNumber.toLowerCase())) return false;
    if (filters.invoiceType === 'planned' && !inv.expected_date) return false;
    if (filters.invoiceType === 'actual' && inv.expected_date) return false;
    if (filters.arrivalFrom || filters.arrivalTo) {
      let arrival = null;
      if (inv.status === 'planned') arrival = inv.expected_date;
      else if (inv.status === 'accepted' && inv.accepted_at) arrival = inv.accepted_at.split('T')[0];
      if (!arrival) return false;
      if (filters.arrivalFrom && arrival < filters.arrivalFrom) return false;
      if (filters.arrivalTo && arrival > filters.arrivalTo) return false;
    }
    return true;
  }, [filters]);

  // После создания/редактирования перезагружаем список с сервера, чтобы получить
  // правильно отсортированный набор (бэкенд сортирует по drafts → planned → accepted
  // с разными правилами внутри групп — на клиенте дублировать не хочется).
  const handleInvoiceSaved = useCallback((savedInvoice) => {
    setCreateInvoiceType(null);
    setEditingInvoice(null);
    if (savedInvoice && savedInvoice.id && !matchesActiveFilters(savedInvoice)) {
      showToast('Сохранено. Не отображается из-за активных фильтров', 'info');
    }
    loadData();
  }, [loadData, matchesActiveFilters, showToast]);

  // Состояния раскрытия инлайн-фильтров в шапке таблицы
  const [numberSearchOpen, setNumberSearchOpen] = useState(false);
  // Поиск по номеру: фильтр серверный, поэтому в filters (= запрос страницы) значение попадает
  // с задержкой после последней клавиши, как в «Клиентах», а не на каждый символ
  const [invoiceNumberInput, setInvoiceNumberInput] = useState('');
  const debouncedInvoiceNumber = useDebouncedValue(invoiceNumberInput.trim(), 300);
  useEffect(() => {
    setFilters(f => (f.invoiceNumber === debouncedInvoiceNumber ? f : { ...f, invoiceNumber: debouncedInvoiceNumber }));
  }, [debouncedInvoiceNumber]);
  const [showDateCalendar, setShowDateCalendar] = useState(false);
  const [showArrivalCalendar, setShowArrivalCalendar] = useState(false);
  const dateCalendarTriggerRef = useRef(null);
  const arrivalCalendarTriggerRef = useRef(null);
  const fmtDateLabel = (from, to) => {
    const short = (d) => { const [, m, day] = d.split('-'); return `${day}.${m}`; };
    const full = (d) => { const [y, m, day] = d.split('-'); return `${day}.${m}.${y}`; };
    if (from && to) return `${short(from)} — ${short(to)}`;
    if (from) return `с ${full(from)}`;
    if (to) return `до ${full(to)}`;
    return null;
  };
  const dateLabel = fmtDateLabel(filters.dateFrom, filters.dateTo);
  const arrivalLabel = fmtDateLabel(filters.arrivalFrom, filters.arrivalTo);

  const clearBtnStyle = {
    width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%',
    cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  return (
    <>
      {/* Table */}
      {isLoading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="table-container" style={{ marginTop: 16 }}>
          <table className="table" style={{ tableLayout: 'fixed', minWidth: 1080 }}>
            {/* Процентные ширины — все колонки масштабируются пропорционально при зуме */}
            <colgroup>
              <col style={{ width: '5%' }} />{/* ID */}
              <col style={{ width: '15%' }} />{/* № накл. — номера поставщиков бывают длинными */}
              <col style={{ width: '9%' }} />{/* Тип */}
              <col style={{ width: '10%' }} />{/* Статус */}
              <col style={{ width: '10%' }} />{/* Дата создания */}
              <col style={{ width: '11%' }} />{/* Дата поступления */}
              <col />{/* Поставщик — flex */}
              <col style={{ width: '6%' }} />{/* Позиций */}
              <col style={{ width: '5%' }} />{/* Брак */}
              <col style={{ width: '8%' }} />{/* Сумма */}
            </colgroup>
            <thead>
              <tr>
                <th>ID</th>
                {/* № накладной — поиск по номеру с раскрытием */}
                <th style={{ position: 'relative' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, visibility: (numberSearchOpen || invoiceNumberInput) ? 'hidden' : 'visible' }}>
                    <span>№ накл.</span>
                    <button
                      onClick={() => setNumberSearchOpen(true)}
                      title="Поиск по номеру"
                      style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#1C3D2E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', '--ch': '5px'}}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(28,61,46,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                    </button>
                  </div>
                  {(numberSearchOpen || invoiceNumberInput) && (
                    <div style={{ position: 'absolute', left: 12, right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                      <input
                        autoFocus
                        type="text"
                        placeholder="№ накладной"
                        value={invoiceNumberInput}
                        onChange={e => setInvoiceNumberInput(e.target.value)}
                        onBlur={() => { if (!invoiceNumberInput) setNumberSearchOpen(false); }}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '6px 24px 6px 10px', border: '1px solid #1C3D2E', '--ch': '6px', '--stroke': '#1C3D2E', fontSize: 13, fontWeight: 500, outline: 'none', background: '#fff', color: '#1C3D2E', textTransform: 'none', letterSpacing: '0.01em' }}
                      />
                      {invoiceNumberInput && (
                        <button onClick={() => { setInvoiceNumberInput(''); setFilters(f => ({ ...f, invoiceNumber: '' })); setNumberSearchOpen(false); }}
                          style={{ ...clearBtnStyle, position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)' }}
                        >×</button>
                      )}
                    </div>
                  )}
                </th>
                {/* Тип */}
                <th>
                  <HeaderSelect
                    label="Тип"
                    value={filters.invoiceType}
                    options={[
                      { value: 'planned', label: 'Плановая' },
                      { value: 'actual', label: 'Приходная' },
                    ]}
                    onChange={(v) => setFilters(f => ({ ...f, invoiceType: v || '' }))}
                  />
                </th>
                {/* Статус — сразу после Типа */}
                <th>
                  <HeaderSelect
                    label="Статус"
                    value={filters.status}
                    options={[
                      { value: 'accepted', label: 'Принята' },
                      { value: 'draft', label: 'Черновик' },
                      { value: 'planned', label: 'Ожидает' },
                    ]}
                    onChange={(v) => setFilters(f => ({ ...f, status: v || '' }))}
                  />
                </th>
                {/* Дата создания — period filter */}
                <th>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <button
                      ref={dateCalendarTriggerRef}
                      type="button"
                      onClick={() => setShowDateCalendar(v => !v)}
                      title={dateLabel || ''}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: 0, border: 'none', background: 'transparent',
                        color: dateLabel ? '#1C3D2E' : 'inherit',
                        fontWeight: dateLabel ? 700 : 'inherit',
                        fontSize: 'inherit', fontFamily: 'inherit',
                        letterSpacing: 'inherit', textTransform: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      <span>{dateLabel || 'Дата создания'}</span>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </button>
                    {dateLabel && (
                      <button onClick={(e) => { e.stopPropagation(); setFilters(f => ({ ...f, dateFrom: '', dateTo: '' })); }}
                        title="Сбросить" style={clearBtnStyle}
                      >×</button>
                    )}
                  </span>
                  {showDateCalendar && (
                    <DateRangeCalendar
                      anchorEl={dateCalendarTriggerRef}
                      dateFrom={filters.dateFrom}
                      dateTo={filters.dateTo}
                      onSelect={(from, to) => { setFilters(f => ({ ...f, dateFrom: from, dateTo: to })); setShowDateCalendar(false); }}
                      onClose={() => setShowDateCalendar(false)}
                    />
                  )}
                </th>
                {/* Дата поступления — period filter */}
                <th>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <button
                      ref={arrivalCalendarTriggerRef}
                      type="button"
                      onClick={() => setShowArrivalCalendar(v => !v)}
                      title={arrivalLabel || ''}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: 0, border: 'none', background: 'transparent',
                        color: arrivalLabel ? '#1C3D2E' : 'inherit',
                        fontWeight: arrivalLabel ? 700 : 'inherit',
                        fontSize: 'inherit', fontFamily: 'inherit',
                        letterSpacing: 'inherit', textTransform: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      <span>{arrivalLabel || 'Дата поступления'}</span>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </button>
                    {arrivalLabel && (
                      <button onClick={(e) => { e.stopPropagation(); setFilters(f => ({ ...f, arrivalFrom: '', arrivalTo: '' })); }}
                        title="Сбросить" style={clearBtnStyle}
                      >×</button>
                    )}
                  </span>
                  {showArrivalCalendar && (
                    <DateRangeCalendar
                      anchorEl={arrivalCalendarTriggerRef}
                      dateFrom={filters.arrivalFrom}
                      dateTo={filters.arrivalTo}
                      onSelect={(from, to) => { setFilters(f => ({ ...f, arrivalFrom: from, arrivalTo: to })); setShowArrivalCalendar(false); }}
                      onClose={() => setShowArrivalCalendar(false)}
                    />
                  )}
                </th>
                {/* Поставщик — multi-select */}
                <th>
                  <HeaderSelect
                    label="Поставщик"
                    multi
                    value={filters.supplierId.map(String)}
                    options={suppliers.map(s => ({ value: String(s.id), label: s.name }))}
                    onChange={(arr) => setFilters(f => ({ ...f, supplierId: arr || [] }))}
                  />
                </th>
                <th>Позиций</th>
                <th>Брак</th>
                <th>Сумма</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>
                    {(() => {
                      const hasFilters = filters.dateFrom || filters.dateTo
                        || filters.arrivalFrom || filters.arrivalTo
                        || filters.supplierId.length > 0
                        || filters.status || filters.invoiceType
                        || filters.invoiceNumber;
                      return hasFilters
                        ? 'Нет накладных удовлетворяющих выставленным фильтрам'
                        : 'Накладных пока нет';
                    })()}
                  </td>
                </tr>
              ) : invoices.map(inv => {
                // Дата поступления:
                //   planned   → expected_date (запланированная);
                //   accepted  → accepted_at (фактическая);
                //   draft     → "—" (ещё не приняли).
                let arrivalDate = null;
                let arrivalIsPlanned = false;
                if (inv.status === 'planned') {
                  arrivalDate = inv.expected_date;
                  arrivalIsPlanned = true;
                } else if (inv.status === 'accepted' && inv.accepted_at) {
                  arrivalDate = inv.accepted_at.split('T')[0];
                }
                return (
                <tr key={inv.id}
                  className="clickable-row"
                  onClick={() => handleViewInvoice(inv.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ padding: '8px 12px', color: '#5E6561', fontSize: 13, fontFamily: 'monospace' }}>
                    #{inv.id}
                  </td>
                  <td title={inv.invoice_number || undefined} style={{ padding: '8px 12px', color: inv.invoice_number ? '#333' : '#ccc', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inv.invoice_number || '—'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {inv.expected_date ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', '--ch': '5px',
                        fontSize: 12, fontWeight: 600,
                        background: '#F0F8FF', color: '#26689A',
                      }}>Плановая</span>
                    ) : (
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', '--ch': '5px',
                        fontSize: 12, fontWeight: 600,
                        background: '#DFF2E8', color: '#1C3D2E',
                      }}>Приходная</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                    <span className={`badge ${statusBadge[inv.status] || ''}`}>
                      {statusLabel[inv.status] || inv.status}
                    </span>
                    {inv.status === 'planned' && inv.is_overdue && (
                      <span style={{ display: 'block', marginTop: 3, fontSize: 11, fontWeight: 700, color: '#CB4D45', textTransform: 'uppercase', letterSpacing: 0.3 }}>просрочена</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px' }}>{inv.created_at ? <DocDateTime ts={inv.created_at} /> : formatDate(inv.date)}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 500, color: arrivalIsPlanned && inv.is_overdue ? '#CB4D45' : '#1C3D2E' }}>
                    {arrivalDate ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        {/* Принятая — дата и время приёмки; плановая — только ожидаемая дата */}
                        {!arrivalIsPlanned && inv.accepted_at ? <DocDateTime ts={inv.accepted_at} /> : formatDate(arrivalDate)}
                        {arrivalIsPlanned && inv.is_overdue && (
                          <Tooltip text="Не принята к концу приёма заказов на день поставки — в доступность не входит">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                              stroke="#B0603C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                              <line x1="12" y1="9" x2="12" y2="13"/>
                              <line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                          </Tooltip>
                        )}
                      </span>
                    ) : <span style={{ color: '#C1C8C4' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 12px' }}>{inv.supplier_name}</td>
                  <td style={{ padding: '8px 12px' }}>{inv.items_count}</td>
                  <td style={{ padding: '8px 12px', fontWeight: inv.has_defect ? 600 : 400, color: inv.has_defect ? '#B0603C' : '#C1C8C4' }}>
                    {inv.has_defect ? inv.total_defect : '—'}
                  </td>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                    {Number(inv.total_sum).toLocaleString('ru-RU')} ₽
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} />
        </div>
      )}

      {/* Create Modal */}
      {createInvoiceType && (
        <InvoiceFormModal
          suppliers={suppliers}
          initialPlanned={createInvoiceType === 'planned'}
          onClose={() => setCreateInvoiceType(null)}
          onSaved={handleInvoiceSaved}
          showToast={showToast}
        />
      )}

      {/* Edit Modal */}
      {editingInvoice && (
        <InvoiceFormModal
          suppliers={suppliers}
          editingInvoice={editingInvoice}
          onClose={() => {
            // Cancel: если открыли из view-модалки — возвращаемся обратно с прежними данными
            if (editingFromView) {
              setViewInvoice(editingInvoice);
            }
            setEditingInvoice(null);
            setEditingFromView(false);
          }}
          onSaved={(saved) => {
            // Save: если открыли из view — возвращаемся в view с обновлёнными данными
            if (editingFromView && saved) {
              setViewInvoice(saved);
            }
            setEditingFromView(false);
            handleInvoiceSaved(saved);  // закрывает edit + обновляет список накладных
          }}
          showToast={showToast}
        />
      )}

      {/* View Modal */}
      {viewInvoice && !editingInvoice && (
        <InvoiceViewModal
          invoice={viewInvoice}
          onClose={() => setViewInvoice(null)}
          onEdit={() => {
            const id = viewInvoice.id;
            setEditingFromView(true);
            setViewInvoice(null);
            handleEditInvoice(id);
          }}
          onDelete={() => {
            const id = viewInvoice.id;
            setViewInvoice(null);
            handleDelete(id);
          }}
          onAccept={() => {
            setViewInvoice(null);
            handleAccept(viewInvoice.id);
          }}
          onAcceptPlanned={(inv) => {
            setViewInvoice(null);
            setAcceptingInvoice(inv);
          }}
        />
      )}

      {/* Accept Planned Modal */}
      {acceptingInvoice && (
        <AcceptPlannedModal
          invoice={acceptingInvoice}
          onClose={() => setAcceptingInvoice(null)}
          onAccepted={() => { setAcceptingInvoice(null); loadData(); }}
          showToast={showToast}
        />
      )}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </>
  );
}

// ---- Invoice Create/Edit Modal ----
function InvoiceFormModal({ suppliers, editingInvoice, initialPlanned, onClose, onSaved, showToast }) {
  const isEditing = !!editingInvoice;
  const isPlanned = isEditing ? editingInvoice.status === 'planned' : !!initialPlanned;
  const [supplierId, setSupplierId] = useState(
    isEditing ? editingInvoice.supplier_id : ''
  );
  const [date, setDate] = useState(
    isEditing ? editingInvoice.date : getLocalToday()
  );
  const [invoiceNumber, setInvoiceNumber] = useState(
    isEditing ? (editingInvoice.invoice_number || '') : ''
  );
  const [comment, setComment] = useState(
    isEditing ? (editingInvoice.comment || '') : ''
  );
  const [expectedDate, setExpectedDate] = useState(
    isEditing ? (editingInvoice.expected_date || '') : ''
  );
  const [extraDays, setExtraDays] = useState(
    isEditing ? (editingInvoice.extra_days || 0) : 0
  );
  const [availablePercent, setAvailablePercent] = useState(
    isEditing ? (editingInvoice.available_percent ?? '') : ''
  );
  const [items, setItems] = useState(
    isEditing
      ? editingInvoice.items.map(it => {
          const aq = it.available_quantity ?? '';
          const pct = editingInvoice.available_percent;
          const autoVal = (pct != null && it.quantity)
            ? Math.floor(it.quantity * pct / 100)
            : null;
          const isManual = aq !== '' && aq !== null && (autoVal === null || parseInt(aq) !== autoVal);
          return {
            product_id: it.product_id,
            product_name: it.product_name,
            shelf_life_days: it.shelf_life_days || null,
            quantity: it.quantity,
            purchase_price: it.purchase_price,
            defect_quantity: it.defect_quantity || 0,
            available_quantity: aq,
            available_quantity_manual: isManual,
            reserved_quantity: it.reserved_quantity || 0,
          };
        })
      : []
  );
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    api.getProducts(1, 500).then(data => setAllProducts(data.items || data)).catch(() => {});
  }, []);

  // Карта физ-остатков: показываем «сколько реально на складе сейчас», без future_stock.
  const physicalQtyMap = useMemo(() => {
    const map = {};
    allProducts.forEach(p => { map[String(p.id)] = p.physical_quantity || 0; });
    return map;
  }, [allProducts]);

  const addProduct = (product) => {
    const autoVal = (isPlanned && availablePercent !== '')
      ? Math.floor(1 * parseInt(availablePercent) / 100)
      : '';
    setItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      shelf_life_days: product.shelf_life_days || null,
      quantity: 1,
      purchase_price: 0,
      defect_quantity: 0,
      available_quantity: autoVal,
      available_quantity_manual: false,
    }]);
    setShowProductSearch(false);
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'quantity' && !item.available_quantity_manual && isPlanned && availablePercent !== '') {
        const newQty = parseInt(value) || 0;
        updated.available_quantity = Math.floor(newQty * parseInt(availablePercent) / 100);
      }
      return updated;
    }));
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const totalSum = items.reduce((sum, it) => sum + (parseInt(it.quantity) || 0) * (parseFloat(String(it.purchase_price).replace(',', '.')) || 0), 0);

  const handleSubmit = async () => {
    if (!supplierId) { showToast('Выберите поставщика', 'error'); return; }
    if (isPlanned && !expectedDate) { showToast('Укажите дату плановой поставки', 'error'); return; }
    if (!items.length) { showToast('Добавьте хотя бы одну позицию', 'error'); return; }

    const parsedItems = items.map(it => ({
      product_id: it.product_id,
      quantity: parseInt(it.quantity) || 0,
      purchase_price: parseFloat(String(it.purchase_price).replace(',', '.')) || 0,
      defect_quantity: parseInt(it.defect_quantity) || 0,
      available_quantity: it.available_quantity !== '' && it.available_quantity !== null && it.available_quantity !== undefined
        ? parseInt(it.available_quantity)
        : null,
    }));

    for (const item of parsedItems) {
      if (item.quantity <= 0) { showToast('Количество должно быть > 0', 'error'); return; }
      if (item.purchase_price < 0) { showToast('Цена не может быть отрицательной', 'error'); return; }
      if (item.defect_quantity < 0 || item.defect_quantity > item.quantity) {
        showToast('Брак не может быть больше количества', 'error'); return;
      }
      if (item.available_quantity !== null && item.available_quantity > item.quantity) {
        showToast('Доступно для заказа не может быть больше количества', 'error'); return;
      }
    }

    const payload = {
      supplier_id: Number(supplierId),
      date,
      invoice_number: invoiceNumber.trim() || null,
      comment: comment || null,
      items: parsedItems,
    };

    if (isPlanned) {
      payload.status = 'planned';
      payload.expected_date = expectedDate;
      payload.extra_days = parseInt(extraDays) || 0;
      payload.available_percent = availablePercent !== '' ? parseInt(availablePercent) : null;
    }

    setSaving(true);
    try {
      let saved;
      if (isEditing) {
        saved = await api.updateInvoice(editingInvoice.id, payload);
        if (saved?.warnings?.length) {
          showToast('Накладная обновлена. Предупреждения: ' + saved.warnings.join('; '), 'warning');
        } else {
          showToast('Накладная обновлена');
        }
      } else {
        saved = await api.createInvoice(payload);
        showToast(isPlanned ? 'Плановая поставка создана' : 'Накладная создана (черновик)');
      }
      onSaved(saved);
    } catch (error) {
      showToast(error.message || 'Ошибка сохранения', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEditing ? 'Редактирование накладной' : isPlanned ? 'Новая плановая накладная' : 'Новая приходная накладная'}
      size="xl"
      onClose={onClose}
      confirmOverlayClose="Закрыть накладную? Введённые данные не сохранятся."
      footer={
        <>
          {/* Как во всех окнах: [Отмена] [Главная] (UX-аудит 25.10) */}
          <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Сохранение...' : isEditing ? 'Сохранить' : isPlanned ? 'Создать' : 'Создать черновик'}
          </button>
        </>
      }
    >
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Поставщик</label>
          <Select
            value={supplierId}
            onChange={(v) => setSupplierId(v)}
            options={suppliers.map(s => ({ value: s.id, label: s.name }))}
            placeholder="— Выберите поставщика —"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Дата</label>
          <DatePickerCalendar value={date} onChange={setDate} placeholder="Выберите дату" />
        </div>
        <div className="form-group">
          <label className="form-label">Номер накладной {!isPlanned && '*'}</label>
          <input type="text" className="form-input" placeholder="Введите номер"
            value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Комментарий</label>
          <input type="text" className="form-input" placeholder="Необязательно"
            value={comment} onChange={e => setComment(e.target.value)} />
        </div>
      </div>

      {isPlanned && (
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Ожидаемая дата поставки *</label>
            <DatePickerCalendar
              value={expectedDate}
              onChange={setExpectedDate}
              placeholder="дд.мм.гггг"
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label" title="Заказы открываются через N дней после даты поставки">Буфер (дней до открытия)</label>
            <input type="number" className="form-input" min={0} max={30} value={extraDays}
              onChange={e => setExtraDays(e.target.value)} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label" title="% от поставки, доступный для заказов (пусто = 100%)">% доступности (пусто = 100%)</label>
            <input type="number" className="form-input" min={1} max={100} placeholder="100"
              value={availablePercent} onChange={e => {
                const newPct = e.target.value;
                setAvailablePercent(newPct);
                setItems(prev => prev.map(item => {
                  if (item.available_quantity_manual) return item;
                  const qty = parseInt(item.quantity) || 0;
                  return {
                    ...item,
                    available_quantity: newPct !== '' ? Math.floor(qty * parseInt(newPct) / 100) : '',
                  };
                }));
              }} />
          </div>
        </div>
      )}

      {/* Items */}
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <label className="form-label" style={{ margin: 0 }}>Позиции</label>
          {/* «+ Добавить товар» — ссылка-действие, как во всех формах склада (UX-аудит 25.10) */}
          <button type="button" className="btn-link" onClick={() => setShowProductSearch(true)}>
            + Добавить товар
          </button>
        </div>

        {showProductSearch && (
          <div style={{ marginBottom: 12 }}>
            <ProductSearchDropdown
              onSelect={addProduct}
              excludeIds={items.map(i => i.product_id)}
              onClose={() => setShowProductSearch(false)}
              maxQtyMap={physicalQtyMap}
              availabilityLabel="На складе"
            />
          </div>
        )}

        {items.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', padding: 20, fontSize: 13 }}>
            Добавьте товары в накладную
          </div>
        ) : (
          <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9F9F6' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Товар</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 80 }}>Кол-во</th>
                  {isPlanned && isEditing && <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 85 }}>Зарезерв.</th>}
                  {isPlanned && <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Лимит продаж</th>}
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 100 }}>Цена закупки</th>
                  {!isPlanned && <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 70 }}>Брак</th>}
                  {!isPlanned && <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 110 }} title="Текущий физ. остаток + (Кол-во − Брак)">Кол. после приёмки</th>}
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, width: 90 }}>Сумма</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid #EBEDEC' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 500 }}>
                      {item.product_name}
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <input type="text" inputMode="numeric" value={item.quantity}
                        onFocus={e => e.target.select()}
                        onChange={e => { const v = e.target.value.replace(/\D/g, ''); updateItem(idx, 'quantity', v === '' ? '' : parseInt(v)); }}
                        onBlur={() => { if (!item.quantity) updateItem(idx, 'quantity', 1); }}
                        style={{ width: '100%', padding: '4px 6px', textAlign: 'center', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13 }}
                      />
                    </td>
                    {isPlanned && isEditing && (
                      <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 500,
                        color: (item.reserved_quantity || 0) > 0 ? '#26689A' : '#bbb' }}>
                        {item.reserved_quantity || 0}
                      </td>
                    )}
                    {isPlanned && (
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <input type="text" inputMode="numeric" value={item.available_quantity ?? ''}
                          placeholder="—"
                          onFocus={e => e.target.select()}
                          onChange={e => {
                            const v = e.target.value.replace(/\D/g, '');
                            // При вводе всегда ставим manual=true, чтобы не мешать набору
                            setItems(prev => prev.map((it, i) => i === idx
                              ? { ...it, available_quantity: v === '' ? '' : parseInt(v), available_quantity_manual: true }
                              : it
                            ));
                          }}
                          onBlur={() => {
                            // Если поле оставили пустым — сбрасываем в авто
                            if (item.available_quantity === '' || item.available_quantity === null) {
                              const autoVal = availablePercent !== ''
                                ? Math.floor((parseInt(item.quantity) || 0) * parseInt(availablePercent) / 100)
                                : '';
                              setItems(prev => prev.map((it, i) => i === idx
                                ? { ...it, available_quantity: autoVal, available_quantity_manual: false }
                                : it
                              ));
                            }
                          }}
                          style={{
                            width: '100%', padding: '4px 6px', textAlign: 'center',
                            border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13,
                            background: (!item.available_quantity_manual && availablePercent !== '') ? '#EEF6FF' : '#fff',
                          }}
                        />
                      </td>
                    )}
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <input type="text" inputMode="decimal" value={String(item.purchase_price).replace('.', ',')}
                        onFocus={e => e.target.select()}
                        onChange={e => { const v = e.target.value.replace(/[^\d,]/g, '').replace(/(,.*),/g, '$1'); updateItem(idx, 'purchase_price', v); }}
                        onBlur={() => { const n = parseFloat(String(item.purchase_price).replace(',', '.')) || 0; updateItem(idx, 'purchase_price', n ? String(n).replace('.', ',') : '0'); }}
                        style={{ width: '100%', padding: '4px 6px', textAlign: 'center', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13 }}
                      />
                    </td>
                    {!isPlanned && (
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <input type="text" inputMode="numeric" value={item.defect_quantity}
                          onFocus={e => e.target.select()}
                          onChange={e => { const v = e.target.value.replace(/\D/g, ''); updateItem(idx, 'defect_quantity', v === '' ? '' : parseInt(v)); }}
                          onBlur={() => { if (item.defect_quantity === '') updateItem(idx, 'defect_quantity', 0); }}
                          style={{ width: '100%', padding: '4px 6px', textAlign: 'center', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13 }}
                        />
                      </td>
                    )}
                    {!isPlanned && (
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#555' }}
                          title="Текущий физ. остаток + (Кол-во − Брак)">
                        {(physicalQtyMap[String(item.product_id)] || 0)
                          + Math.max(0, (parseInt(item.quantity) || 0) - (parseInt(item.defect_quantity) || 0))}
                      </td>
                    )}
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>
                      {((parseInt(item.quantity) || 0) * (parseFloat(String(item.purchase_price).replace(',', '.')) || 0)).toLocaleString('ru-RU')} ₽
                    </td>
                    <td style={{ padding: '4px' }}>
                      {/* Убрать позицию — красная корзина, одинаково во всех формах (UX-аудит 25.10) */}
                      <button type="button" className="btn-icon is-danger" title="Убрать" aria-label="Убрать"
                        onClick={() => removeItem(idx)}>
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 10px', background: '#F9F9F6', borderTop: '1px solid #eee', fontWeight: 600, fontSize: 14 }}>
              Итого: {totalSum.toLocaleString('ru-RU')} ₽
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---- Invoice View Modal ----
function InvoiceViewModal({ invoice, onClose, onEdit, onDelete, onAccept, onAcceptPlanned }) {
  const totalSum = invoice.items.reduce((sum, it) => sum + it.quantity * it.purchase_price, 0);
  const isPlanned = invoice.status === 'planned';
  const isEditable = invoice.status === 'draft' || invoice.status === 'planned';

  return (
    <Modal
      title={`Накладная${invoice.invoice_number ? ' №' + invoice.invoice_number : ''} от ${docDateTimeText(invoice.date, invoice.created_at)}`}
      size="large"
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          {/* Удаление — слева, красный контур (UX-аудит 25.10) */}
          {isEditable ? (
            <button type="button" onClick={onDelete} className="btn btn-danger-outline">
              Удалить накладную
            </button>
          ) : <span />}
          {/* Основные действия — справа */}
          <div style={{ display: 'flex', gap: 8 }}>
            {isEditable && (
              <button className="btn btn-secondary" onClick={onEdit}>Редактировать</button>
            )}
            {/* Приёмка — главное действие накладной (UX-аудит 25.10) */}
            {invoice.status === 'draft' && (
              <button className="btn btn-primary" onClick={() => onAccept()}>Принять на склад</button>
            )}
            {isPlanned && (
              <button className="btn btn-primary" onClick={() => onAcceptPlanned(invoice)}>
                Принять поставку
              </button>
            )}
            {/* Принятая накладная — действий нет, но выход из окна есть всегда (владелец 25.10) */}
            {!isEditable && invoice.status !== 'draft' && !isPlanned && (
              <button type="button" className="btn btn-secondary" onClick={onClose}>Закрыть</button>
            )}
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', gap: 24, marginBottom: 16, fontSize: 14, flexWrap: 'wrap' }}>
        <div><strong>Поставщик:</strong> {invoice.supplier_name}</div>
        {invoice.invoice_number && (
          <div><strong>№:</strong> {invoice.invoice_number}</div>
        )}
        <div>
          <strong>Статус:</strong>{' '}
          <span className={`badge ${statusBadge[invoice.status] || 'badge-new'}`}>
            {statusLabel[invoice.status] || invoice.status}
          </span>
          {invoice.is_overdue && (
            <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 4, color: '#B0603C', fontWeight: 500, fontSize: 12 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="#B0603C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Просрочена
            </span>
          )}
        </div>
        {invoice.created_by_name && (
          <div><strong>Создал:</strong> {invoice.created_by_name}</div>
        )}
        {isPlanned && invoice.expected_date && (
          <div><strong>Ожидаемая дата поставки:</strong> {formatDate(invoice.expected_date)}</div>
        )}
      </div>
      {invoice.comment && (
        <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
          {invoice.comment}
        </div>
      )}

      {/* Таблица позиций */}
      <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9F9F6' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Товар</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 80 }}>Кол-во</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 100 }}>Цена</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 70 }}>Брак</th>
              {isPlanned && <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Зарезерв.</th>}
              {isPlanned && <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Лимит продаж</th>}
              {!isPlanned && <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 80 }}>Факт</th>}
              <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, width: 90 }}>Сумма</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map(item => (
              <tr key={item.id} style={{ borderTop: '1px solid #EBEDEC' }}>
                <td style={{ padding: '8px 10px' }}>{item.product_name}</td>
                <td style={{ padding: '8px 10px', textAlign: 'center' }}>{item.quantity}</td>
                <td style={{ padding: '8px 10px', textAlign: 'center' }}>{item.purchase_price} ₽</td>
                <td style={{ padding: '8px 10px', textAlign: 'center', color: item.defect_quantity ? '#CB4D45' : '#999' }}>
                  {item.defect_quantity || 0}
                </td>
                {isPlanned && (
                  <td style={{ padding: '8px 10px', textAlign: 'center', color: item.reserved_quantity > 0 ? '#26689A' : '#999' }}>
                    {item.reserved_quantity || 0}
                  </td>
                )}
                {isPlanned && (
                  <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 500, color: (item.available_for_order || 0) > 0 ? '#1F763B' : '#999' }}>
                    {item.available_for_order ?? '—'}
                  </td>
                )}
                {!isPlanned && (
                  <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 500 }}>
                    {(item.actual_quantity ?? item.quantity) - (item.defect_quantity || 0)}
                  </td>
                )}
                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>
                  {(item.quantity * item.purchase_price).toLocaleString('ru-RU')} ₽
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 10px', background: '#F9F9F6', borderTop: '1px solid #eee', fontWeight: 600, fontSize: 14 }}>
          Итого: {totalSum.toLocaleString('ru-RU')} ₽
        </div>
      </div>

      {/* История статусов */}
      {invoice.status_history && invoice.status_history.length > 0 && (
        <div style={{ marginTop: 16, borderTop: '1px solid #EBEDEC', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            История изменений
          </div>
          {invoice.status_history.map((h, i) => {
            // Новый формат: action из {created, edited, accepted}
            // Старые legacy-записи (action=NULL) — fallback на from→to
            let title;
            if (h.action === 'created' && h.to_status === 'planned') {
              title = 'Создание плановой накладной';
            } else if (h.action === 'created' && h.to_status === 'accepted') {
              title = 'Создание приходной накладной';
            } else if (h.action === 'edited') {
              title = 'Редактирование плановой накладной';
            } else if (h.action === 'accepted') {
              title = 'Приёмка плановой накладной';
            } else {
              title = h.from_status
                ? `${statusLabel[h.from_status] || h.from_status} → ${statusLabel[h.to_status] || h.to_status}`
                : `Создание: ${statusLabel[h.to_status] || h.to_status}`;
            }
            return (
              <div key={h.id || i} style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: '#aaa', whiteSpace: 'nowrap' }}>
                  {new Date(h.changed_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
                <span>
                  {h.employee_name || 'Система'}
                  {' — '}
                  <strong>{title}</strong>
                  {h.details && <span style={{ color: '#888' }}> ({h.details})</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ---- Accept Planned Invoice Modal ----
function AcceptPlannedModal({ invoice, onClose, onAccepted, showToast }) {
  const [invoiceNumber, setInvoiceNumber] = useState(invoice.invoice_number || '');
  const [actualDate, setActualDate] = useState(getLocalToday());
  const [comment, setComment] = useState(invoice.comment || '');
  const [acceptItems, setAcceptItems] = useState(
    invoice.items.map(it => ({
      invoice_item_id: it.id,
      product_id: it.product_id,
      product_name: it.product_name,
      planned_quantity: it.quantity,
      available_quantity: it.available_quantity,
      reserved_quantity: it.reserved_quantity || 0,
      purchase_price: it.purchase_price,
      actual_quantity: it.quantity,
      defect_quantity: 0,
      isNew: false,
    }))
  );
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    api.getProducts(1, 500).then(data => setAllProducts(data.items || data)).catch(() => {});
  }, []);

  const physicalQtyMap = useMemo(() => {
    const map = {};
    allProducts.forEach(p => { map[String(p.id)] = p.physical_quantity || 0; });
    return map;
  }, [allProducts]);

  const updateItem = (idx, field, value) => {
    setAcceptItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const removeItem = (idx) => {
    setAcceptItems(prev => prev.filter((_, i) => i !== idx));
  };

  const addProduct = (product) => {
    setAcceptItems(prev => [...prev, {
      invoice_item_id: null,
      product_id: product.id,
      product_name: product.name,
      planned_quantity: null,
      available_quantity: null,
      reserved_quantity: 0,
      purchase_price: 0,
      actual_quantity: 1,
      defect_quantity: 0,
      isNew: true,
    }]);
    setShowProductSearch(false);
  };

  const handleAcceptClick = () => {
    if (!invoiceNumber.trim()) {
      showToast('Укажите номер накладной', 'error');
      return;
    }
    setConfirmDialog({
      title: 'Принять поставку?',
      message: 'После принятия изменить введённые данные будет невозможно. Товары будут зачислены на склад.',
      confirmText: 'Принять поставку',
      variant: 'success',
      onConfirm: doAccept,
    });
  };

  const doAccept = async () => {
    setConfirmDialog(null);
    setAccepting(true);
    try {
      const body = {
        items: acceptItems.map(it => ({
          invoice_item_id: it.invoice_item_id,
          product_id: it.isNew ? it.product_id : undefined,
          actual_quantity: parseInt(it.actual_quantity) || 0,
          defect_quantity: parseInt(it.defect_quantity) || 0,
          purchase_price: parseFloat(String(it.purchase_price).replace(',', '.')) || 0,
        })),
        invoice_number: invoiceNumber.trim(),
        date: actualDate,
        comment: comment.trim() || null,
      };
      const result = await api.acceptInvoice(invoice.id, body);
      if (result.warnings?.length) {
        showToast('Поставка принята с предупреждениями: ' + result.warnings.join('; '), 'warning');
      } else {
        showToast('Плановая поставка принята, товары зачислены на склад');
      }
      onAccepted();
    } catch (error) {
      showToast(error.message || 'Ошибка приёмки', 'error');
    } finally {
      setAccepting(false);
    }
  };

  const disabledStyle = { background: '#F4F4EF', color: '#888', cursor: 'not-allowed' };

  return (
    <>
      <Modal
        title={`Приёмка поставки — ${invoice.supplier_name}`}
        size="xl"
        onClose={onClose}
        confirmOverlayClose="Закрыть приёмку? Введённые данные не сохранятся."
        footer={
          <>
            {/* Как во всех окнах: [Отмена] [Главная] (UX-аудит 25.10) */}
            <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
            <button className="btn btn-primary" onClick={handleAcceptClick} disabled={accepting}>
              {accepting ? 'Зачисление...' : 'Принять на склад'}
            </button>
          </>
        }
      >
        {/* Строка 1: Поставщик + Дата */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Поставщик</label>
            <input type="text" className="form-input" disabled
              value={invoice.supplier_name || ''} style={disabledStyle} readOnly />
          </div>
          <div className="form-group">
            <label className="form-label">Дата</label>
            <DatePickerCalendar value={invoice.date} onChange={() => {}} disabled />
          </div>
        </div>

        {/* Строка 2: Номер накладной + Комментарий */}
        <div className="form-row" style={{ marginTop: 16 }}>
          <div className="form-group">
            <label className="form-label">Номер накладной</label>
            <input type="text" className="form-input" placeholder="Введите номер"
              value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Комментарий</label>
            <input type="text" className="form-input" placeholder="Необязательно"
              value={comment} onChange={e => setComment(e.target.value)} />
          </div>
        </div>

        {/* Строка 3: Ожидаемая + Фактическая дата поставки */}
        <div className="form-row" style={{ marginTop: 16 }}>
          <div className="form-group">
            <label className="form-label">Ожидаемая дата поставки</label>
            <input type="text" className="form-input" disabled
              value={invoice.expected_date ? formatDate(invoice.expected_date) : '—'}
              style={disabledStyle} readOnly />
          </div>
          <div className="form-group">
            <label className="form-label">Фактическая дата поставки</label>
            <DatePickerCalendar value={actualDate} onChange={setActualDate} placeholder="Выберите дату" maxDate={getLocalToday()} />
          </div>
        </div>

        {/* Таблица позиций */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <label className="form-label" style={{ margin: 0 }}>Позиции</label>
            <button type="button" className="btn-link" onClick={() => setShowProductSearch(true)}>
              + Добавить товар
            </button>
          </div>

          {showProductSearch && (
            <div style={{ marginBottom: 12 }}>
              <ProductSearchDropdown
                onSelect={addProduct}
                excludeIds={acceptItems.map(i => i.product_id)}
                onClose={() => setShowProductSearch(false)}
                maxQtyMap={physicalQtyMap}
                availabilityLabel="На складе"
              />
            </div>
          )}

          <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9F9F6' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Товар</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Запланировано</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 80 }}>Лимит продаж</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 110 }}>Фактически *</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 70 }}>Брак</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 75 }}>Зарезерв.</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 75 }}>На склад</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 110 }} title="Текущий физ. остаток + На склад">Кол. после приёмки</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 100 }}>Цена закупки</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, width: 90 }}>Сумма</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {acceptItems.map((item, idx) => {
                  const net = (parseInt(item.actual_quantity) || 0) - (parseInt(item.defect_quantity) || 0);
                  const toStock = Math.max(0, net - item.reserved_quantity);
                  const warning = net < item.reserved_quantity;
                  const lineSum = (parseInt(item.actual_quantity) || 0) * (parseFloat(String(item.purchase_price).replace(',', '.')) || 0);
                  return (
                    <tr key={item.isNew ? `new-${idx}` : item.invoice_item_id}
                      style={{ borderTop: '1px solid #EBEDEC', background: warning ? '#F9EBCF' : undefined }}>
                      <td style={{ padding: '8px 10px', fontWeight: 500 }}>{item.product_name}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#999' }}>
                        {item.planned_quantity ?? '—'}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#999' }}>
                        {item.available_quantity ?? '—'}
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <input type="text" inputMode="numeric" value={item.actual_quantity}
                          onFocus={e => e.target.select()}
                          onChange={e => { const v = e.target.value.replace(/\D/g, ''); updateItem(idx, 'actual_quantity', v === '' ? '' : parseInt(v)); }}
                          onBlur={() => { if (item.actual_quantity === '') updateItem(idx, 'actual_quantity', 0); }}
                          style={{ width: '100%', padding: '4px 6px', textAlign: 'center', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13 }}
                        />
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <input type="text" inputMode="numeric" value={item.defect_quantity}
                          onFocus={e => e.target.select()}
                          onChange={e => { const v = e.target.value.replace(/\D/g, ''); updateItem(idx, 'defect_quantity', v === '' ? '' : parseInt(v)); }}
                          onBlur={() => { if (item.defect_quantity === '') updateItem(idx, 'defect_quantity', 0); }}
                          style={{ width: '100%', padding: '4px 6px', textAlign: 'center', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13 }}
                        />
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: item.reserved_quantity > 0 ? '#26689A' : '#999' }}>
                        {item.reserved_quantity || 0}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, color: warning ? '#9A4820' : '#1F763B' }}>
                        {toStock}
                        {warning && <span title="Пришло меньше зарезервированного"> ⚠️</span>}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#555' }}
                          title="Текущий физ. остаток + На склад">
                        {(physicalQtyMap[String(item.product_id)] || 0) + toStock}
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <input type="text" inputMode="decimal" value={String(item.purchase_price).replace('.', ',')}
                          onFocus={e => e.target.select()}
                          onChange={e => { const v = e.target.value.replace(/[^\d,]/g, '').replace(/(,.*),/g, '$1'); updateItem(idx, 'purchase_price', v); }}
                          onBlur={() => { const n = parseFloat(String(item.purchase_price).replace(',', '.')) || 0; updateItem(idx, 'purchase_price', n ? String(n).replace('.', ',') : '0'); }}
                          style={{ width: '100%', padding: '4px 6px', textAlign: 'center', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13 }}
                        />
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>
                        {lineSum.toLocaleString('ru-RU')} ₽
                      </td>
                      <td style={{ padding: '4px' }}>
                        {item.isNew && (
                          <button type="button" className="btn-icon is-danger" title="Убрать" aria-label="Убрать"
                            onClick={() => removeItem(idx)}>
                            <TrashIcon />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 10px', background: '#F9F9F6', borderTop: '1px solid #eee', fontWeight: 600, fontSize: 14 }}>
              Итого: {acceptItems.reduce((sum, it) => sum + (parseInt(it.actual_quantity) || 0) * (parseFloat(String(it.purchase_price).replace(',', '.')) || 0), 0).toLocaleString('ru-RU')} ₽
            </div>
          </div>
        </div>
      </Modal>

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </>
  );
}

// ============================================================
// TAB: Списания (Write-offs)
// ============================================================
function WriteOffsTab({ showToast, createTrigger, onCreateHandled }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewWriteOff, setViewWriteOff] = useState(null);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '' });

  // Страницами по 50 с кнопкой «Загрузить ещё» — раньше список приходил целиком
  const fetchPage = useCallback(({ limit, offset }) => api.getWriteOffs({ ...filters, limit, offset }), [filters]);
  const onListError = useCallback(() => showToast('Ошибка загрузки списаний', 'error'), [showToast]);
  const { items: writeOffs, isLoading, hasMore, loadingMore, reload: loadData, loadMore } =
    usePagedList(fetchPage, { onError: onListError });

  // Trigger из tab-strip кнопки в шапке страницы
  useEffect(() => {
    if (createTrigger) {
      setShowCreateModal(true);
      onCreateHandled && onCreateHandled();
    }
  }, [createTrigger, onCreateHandled]);

  // Period-фильтр в заголовке колонки "Дата"
  const [showDateFilter, setShowDateFilter] = useState(false);
  const dateFilterTriggerRef = useRef(null);
  const dateLabel = filters.dateFrom && filters.dateTo
    ? `${formatDate(filters.dateFrom).slice(0,5)} — ${formatDate(filters.dateTo).slice(0,5)}`
    : filters.dateFrom ? `с ${formatDate(filters.dateFrom)}`
    : filters.dateTo ? `до ${formatDate(filters.dateTo)}`
    : null;

  const handleView = async (id) => {
    try {
      const data = await api.getWriteOff(id);
      setViewWriteOff(data);
    } catch {
      showToast('Ошибка загрузки', 'error');
    }
  };

  const today = getLocalToday();

  return (
    <>
      {isLoading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="table-container" style={{ marginTop: 16 }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>ID</th>
                <th>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <button ref={dateFilterTriggerRef} type="button"
                      onClick={() => setShowDateFilter(v => !v)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: dateLabel ? '#1C3D2E' : 'inherit', fontWeight: dateLabel ? 700 : 'inherit', fontSize: 'inherit', fontFamily: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>
                      <span>{dateLabel || 'Дата'}</span>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </button>
                    {dateLabel && (
                      <button onClick={(e) => { e.stopPropagation(); setFilters(f => ({ ...f, dateFrom: '', dateTo: '' })); }}
                        title="Сбросить" style={{ width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%', cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                    )}
                  </span>
                  {showDateFilter && (
                    <DateRangeCalendar anchorEl={dateFilterTriggerRef}
                      dateFrom={filters.dateFrom} dateTo={filters.dateTo}
                      onSelect={(from, to) => { setFilters(f => ({ ...f, dateFrom: from, dateTo: to })); setShowDateFilter(false); }}
                      onClose={() => setShowDateFilter(false)} />
                  )}
                </th>
                <th>Тип</th>
                <th>Позиций</th>
                <th>Кол-во единиц</th>
                <th>Создал</th>
                <th>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {writeOffs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>
                    Списаний пока нет
                  </td>
                </tr>
              ) : writeOffs.map(wo => (
                <tr key={wo.id}
                  className="clickable-row"
                  onClick={() => handleView(wo.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ padding: '8px 12px', color: '#5E6561', fontSize: 13, fontFamily: 'monospace' }}>#{wo.id}</td>
                  <td style={{ padding: '8px 12px' }}><DocDateTime date={wo.date} ts={wo.created_at} /></td>
                  {/* Тип списания: причины позиций акта без повторов («Увядание, → Склад 2») */}
                  <td style={{ padding: '8px 12px' }}>{writeOffTypes(wo) || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>{wo.items_count}</td>
                  <td style={{ padding: '8px 12px' }}>{wo.total_quantity}</td>
                  <td style={{ padding: '8px 12px' }}>{wo.created_by_name || '—'}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: wo.comment ? '#666' : '#C1C8C4', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={wo.comment || ''}>
                    {wo.comment || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} />
        </div>
      )}

      {showCreateModal && (
        <WriteOffCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); loadData(); }}
          showToast={showToast}
        />
      )}

      {viewWriteOff && (
        <WriteOffViewModal
          writeOff={viewWriteOff}
          onClose={() => setViewWriteOff(null)}
        />
      )}
    </>
  );
}

// ---- Write-off Create Modal ----
function WriteOffCreateModal({ onClose, onCreated, showToast }) {
  const [date, setDate] = useState(getLocalToday());
  const [comment, setComment] = useState('');
  const [items, setItems] = useState([]);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [transferPoints, setTransferPoints] = useState([]);

  useEffect(() => {
    api.getProducts(1, 500).then(data => setAllProducts(data.items || data)).catch(() => {});
    api.getTransferPoints().then(setTransferPoints)
      .catch(() => showToast('Не удалось загрузить точки перемещения', 'error'));
  }, []);

  const physicalQtyMap = useMemo(() => {
    const map = {};
    allProducts.forEach(p => { map[String(p.id)] = p.physical_quantity || 0; });
    return map;
  }, [allProducts]);

  const addProduct = (product) => {
    setItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      max_quantity: product.physical_quantity || 0,
      quantity: 1,
      reason: 'wilting',
    }]);
    setShowProductSearch(false);
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Причина в селекте: 'wilting' / 'damage' / 'transfer:<id точки>'
  const setItemReason = (idx, value) => {
    const [reason, pointId] = value.split(':');
    setItems(prev => prev.map((item, i) => i === idx
      ? { ...item, reason, transfer_point_id: pointId ? parseInt(pointId) : null }
      : item));
  };

  const buildPayload = () => ({
    date,
    comment: comment || null,
    items: items.map(({ product_id, quantity, reason, transfer_point_id }) => ({
      product_id, quantity: parseInt(quantity) || 0, reason,
      transfer_point_id: reason === 'transfer' ? transfer_point_id : null,
    })),
  });

  const handleSubmit = async () => {
    if (!items.length) { showToast('Добавьте хотя бы одну позицию', 'error'); return; }

    for (const item of items) {
      const qty = parseInt(item.quantity) || 0;
      if (qty <= 0) { showToast('Количество должно быть > 0', 'error'); return; }
      if (qty > item.max_quantity) {
        showToast(`Нельзя списать больше, чем есть на складе (${item.product_name}: ${item.max_quantity} шт.)`, 'error');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      const preview = await api.previewWriteOff(payload);

      if (preview.has_deficit) {
        setConfirmData({ payload, affected_orders: preview.affected_orders });
        setSaving(false);
        return;
      }

      await api.createWriteOff(payload);
      showToast('Списание создано, товары списаны со склада');
      onCreated();
    } catch (error) {
      showToast(error.message || 'Ошибка создания', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmWriteOff = async () => {
    setSaving(true);
    try {
      await api.createWriteOff(confirmData.payload);
      showToast('Списание создано, товары списаны со склада');
      onCreated();
    } catch (error) {
      showToast(error.message || 'Ошибка создания', 'error');
    } finally {
      setSaving(false);
      setConfirmData(null);
    }
  };

  return (
    <>
    <Modal
      title="Новый акт списания"
      confirmOverlayClose
      size="xl"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button className="btn btn-danger" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Сохранение...' : 'Списать'}
          </button>
        </>
      }
    >
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Дата</label>
          <DatePickerCalendar value={date} onChange={setDate} placeholder="Выберите дату" />
        </div>
        <div className="form-group">
          <label className="form-label">Комментарий</label>
          <input type="text" className="form-input" placeholder="Необязательно"
            value={comment} onChange={e => setComment(e.target.value)} />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <label className="form-label" style={{ margin: 0 }}>Позиции</label>
          <button type="button" className="btn-link" onClick={() => setShowProductSearch(true)}>
            + Добавить товар
          </button>
        </div>

        {showProductSearch && (
          <div style={{ marginBottom: 12 }}>
            <ProductSearchDropdown
              onSelect={addProduct}
              excludeIds={items.map(i => i.product_id)}
              onClose={() => setShowProductSearch(false)}
              maxQtyMap={physicalQtyMap}
              availabilityLabel="На складе"
            />
          </div>
        )}

        {items.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', padding: 20, fontSize: 13 }}>
            Добавьте товары для списания
          </div>
        ) : (
          <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9F9F6' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Товар</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 80 }}>На складе</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 80 }}>Списать</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 150 }}>Причина</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid #EBEDEC' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 500 }}>{item.product_name}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'center', color: '#999' }}>{item.max_quantity}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <input type="text" inputMode="numeric" value={item.quantity}
                        onFocus={e => e.target.select()}
                        onChange={e => { const v = e.target.value.replace(/\D/g, ''); updateItem(idx, 'quantity', v === '' ? '' : parseInt(v)); }}
                        onBlur={() => { if (!item.quantity) updateItem(idx, 'quantity', 1); }}
                        style={{ width: '100%', padding: '4px 6px', textAlign: 'center', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13 }}
                      />
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <select value={item.reason === 'transfer' ? `transfer:${item.transfer_point_id}` : item.reason}
                        onChange={e => setItemReason(idx, e.target.value)}
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13 }}
                      >
                        <option value="wilting">Увядание</option>
                        <option value="damage">Повреждение</option>
                        {transferPoints.length > 0 && (
                          <optgroup label="Перемещение (не потеря)">
                            {transferPoints.map(p => (
                              <option key={p.id} value={`transfer:${p.id}`}>→ {p.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </td>
                    <td style={{ padding: '4px' }}>
                      <button type="button" className="btn-icon is-danger" title="Убрать" aria-label="Убрать"
                        onClick={() => removeItem(idx)}>
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>

    {confirmData && (
      <ConfirmDialog
        title="Списание создаст дефицит"
        message={
          <>
            <span>Следующие заказы потеряют покрытие товаром:</span>
            <ul style={{ margin: '8px 0', paddingLeft: 20, fontSize: 13 }}>
              {confirmData.affected_orders.map((ao, i) => (
                <li key={i}>
                  Заказ #{ao.order_id} — {ao.product_name}: {ao.deficit_quantity} шт.
                  {ao.delivery_date && ` (доставка ${new Date(ao.delivery_date + 'T00:00:00').toLocaleDateString('ru-RU')})`}
                </li>
              ))}
            </ul>
            <div style={{ margin: '8px 0', fontSize: 13, fontWeight: 600 }}>
              Итого дефицит: {confirmData.affected_orders.reduce((s, ao) => s + (ao.deficit_quantity || 0), 0)} шт.
            </div>
            <span>Продолжить списание?</span>
          </>
        }
        confirmText="Списать"
        variant="danger"
        onConfirm={handleConfirmWriteOff}
        onCancel={() => setConfirmData(null)}
      />
    )}
    </>
  );
}

// ---- Write-off View Modal ----
function WriteOffViewModal({ writeOff, onClose }) {
  return (
    <Modal
      title={`Списание №${writeOff.id} от ${docDateTimeText(writeOff.date, writeOff.created_at)}`}
      size="large"
      onClose={onClose}
    >
      {writeOff.created_by_name && (
        <div style={{ marginBottom: 12, fontSize: 14 }}>
          <strong>Создал:</strong> {writeOff.created_by_name}
        </div>
      )}
      {writeOff.comment && (
        <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
          {writeOff.comment}
        </div>
      )}

      <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9F9F6' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Товар</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 80 }}>Кол-во</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 150 }}>Причина</th>
            </tr>
          </thead>
          <tbody>
            {writeOff.items.map(item => (
              <tr key={item.id} style={{ borderTop: '1px solid #EBEDEC' }}>
                <td style={{ padding: '8px 10px' }}>{item.product_name}</td>
                <td style={{ padding: '8px 10px', textAlign: 'center' }}>{item.quantity}</td>
                <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                  {writeOffReasonText(item)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// ============================================================
// TAB: Пересорт (Misgrades)
// ============================================================
function MisgradesTab({ showToast, createTrigger, onCreateHandled }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMisgrade, setViewMisgrade] = useState(null);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '' });

  // Страницами по 50 с кнопкой «Загрузить ещё» — раньше список приходил целиком
  const fetchPage = useCallback(({ limit, offset }) => api.getMisgrades({ ...filters, limit, offset }), [filters]);
  const onListError = useCallback(() => showToast('Ошибка загрузки пересортов', 'error'), [showToast]);
  const { items: misgrades, isLoading, hasMore, loadingMore, reload: loadData, loadMore } =
    usePagedList(fetchPage, { onError: onListError });

  // Trigger из tab-strip кнопки
  useEffect(() => {
    if (createTrigger) {
      setShowCreateModal(true);
      onCreateHandled && onCreateHandled();
    }
  }, [createTrigger, onCreateHandled]);

  // Period-фильтр в заголовке
  const [showDateFilter, setShowDateFilter] = useState(false);
  const dateFilterTriggerRef = useRef(null);
  const dateLabel = filters.dateFrom && filters.dateTo
    ? `${formatDate(filters.dateFrom).slice(0,5)} — ${formatDate(filters.dateTo).slice(0,5)}`
    : filters.dateFrom ? `с ${formatDate(filters.dateFrom)}`
    : filters.dateTo ? `до ${formatDate(filters.dateTo)}`
    : null;

  const handleView = async (id) => {
    try {
      const data = await api.getMisgrade(id);
      setViewMisgrade(data);
    } catch {
      showToast('Ошибка загрузки', 'error');
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="table-container" style={{ marginTop: 16 }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>ID</th>
                <th>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <button ref={dateFilterTriggerRef} type="button"
                      onClick={() => setShowDateFilter(v => !v)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: dateLabel ? '#1C3D2E' : 'inherit', fontWeight: dateLabel ? 700 : 'inherit', fontSize: 'inherit', fontFamily: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>
                      <span>{dateLabel || 'Дата'}</span>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </button>
                    {dateLabel && (
                      <button onClick={(e) => { e.stopPropagation(); setFilters(f => ({ ...f, dateFrom: '', dateTo: '' })); }}
                        title="Сбросить" style={{ width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%', cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                    )}
                  </span>
                  {showDateFilter && (
                    <DateRangeCalendar anchorEl={dateFilterTriggerRef}
                      dateFrom={filters.dateFrom} dateTo={filters.dateTo}
                      onSelect={(from, to) => { setFilters(f => ({ ...f, dateFrom: from, dateTo: to })); setShowDateFilter(false); }}
                      onClose={() => setShowDateFilter(false)} />
                  )}
                </th>
                <th>Откуда</th>
                <th>Куда</th>
                <th>Кол-во</th>
                <th>Создал</th>
                <th>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {misgrades.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>
                    Пересортов пока нет
                  </td>
                </tr>
              ) : misgrades.map(mg => (
                <tr key={mg.id}
                  className="clickable-row"
                  onClick={() => handleView(mg.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ padding: '8px 12px', color: '#5E6561', fontSize: 13, fontFamily: 'monospace' }}>#{mg.id}</td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}><DocDateTime date={mg.date} ts={mg.created_at} /></td>
                  <td style={{ padding: '8px 12px' }}>{mg.from_product_name}</td>
                  <td style={{ padding: '8px 12px' }}>{mg.to_product_name}</td>
                  <td style={{ padding: '8px 12px' }}>{mg.quantity}</td>
                  <td style={{ padding: '8px 12px' }}>{mg.created_by_name || '—'}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: '#666' }}>{mg.comment || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} />
        </div>
      )}

      {showCreateModal && (
        <MisgradeCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); loadData(); }}
          showToast={showToast}
        />
      )}

      {viewMisgrade && (
        <MisgradeViewModal
          misgrade={viewMisgrade}
          onClose={() => setViewMisgrade(null)}
        />
      )}
    </>
  );
}

function MisgradeCreateModal({ onClose, onCreated, showToast }) {
  const [date, setDate] = useState(getLocalToday());
  const [comment, setComment] = useState('');
  const [fromProduct, setFromProduct] = useState(null);
  const [toProduct, setToProduct] = useState(null);
  const [quantity, setQuantity] = useState('1');
  // Себестоимость стебля переходит с исходного товара (25.10): подставляем его закупочную цену, можно поправить
  const [unitCost, setUnitCost] = useState('');
  useEffect(() => {
    if (!fromProduct) { setUnitCost(''); return undefined; }
    let alive = true;
    api.getMisgradeCost(fromProduct.id)
      .then(r => { if (alive) setUnitCost(r.unit_cost != null ? String(r.unit_cost) : ''); })
      .catch(() => {});
    return () => { alive = false; };
  }, [fromProduct]);
  const [showFromSearch, setShowFromSearch] = useState(false);
  const [showToSearch, setShowToSearch] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!fromProduct) { showToast('Выберите исходный товар', 'error'); return; }
    if (!toProduct) { showToast('Выберите целевой товар', 'error'); return; }
    if (fromProduct.id === toProduct.id) { showToast('Нельзя сделать пересорт в тот же товар', 'error'); return; }
    const qty = parseInt(quantity) || 0;
    if (qty <= 0) { showToast('Количество должно быть > 0', 'error'); return; }
    if (qty > (fromProduct.physical_quantity || 0)) {
      showToast(`Недостаточно физ-стока: ${fromProduct.physical_quantity || 0}`, 'error'); return;
    }

    setSaving(true);
    try {
      await api.createMisgrade({
        date,
        from_product_id: fromProduct.id,
        to_product_id: toProduct.id,
        quantity: qty,
        unit_cost: unitCost.trim() === '' ? null : Number(unitCost.replace(',', '.')),
        comment: comment || null,
      });
      showToast('Пересорт создан');
      onCreated();
    } catch (error) {
      showToast(error.message || 'Ошибка создания', 'error');
    } finally {
      setSaving(false);
    }
  };

  const excludeFromIds = toProduct ? [toProduct.id] : [];
  const excludeToIds = fromProduct ? [fromProduct.id] : [];

  return (
    <Modal
      title="Новый пересорт"
      confirmOverlayClose
      size="large"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Сохранение...' : 'Создать'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label className="form-label">Дата</label>
          <DatePickerCalendar value={date} onChange={setDate} placeholder="Выберите дату" />
        </div>
        <div style={{ flex: 2 }}>
          <label className="form-label">Комментарий</label>
          <input type="text" className="form-input" placeholder="Необязательно"
            value={comment} onChange={e => setComment(e.target.value)} />
        </div>
      </div>

      {/* Интерфейс замены */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Откуда */}
        <div style={{ flex: 1 }}>
          <label className="form-label">Откуда (было записано)</label>
          {fromProduct ? (
            <div style={{
              border: '2px solid #CB4D45', '--ch': '6px', '--stroke': '#CB4D45', '--bw': '2px', padding: '10px 12px',
              background: '#FFF4F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{fromProduct.name}</div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                  Физ. остаток: {fromProduct.physical_quantity || 0}
                  {(fromProduct.physical_quantity || 0) !== (fromProduct.quantity || 0) &&
                    ` (доступно после резерваций: ${fromProduct.quantity || 0})`}
                </div>
              </div>
              {/* Убрать выбранный товар — как убрать позицию в формах склада (UX-аудит 25.10) */}
              <button type="button" className="btn-icon is-danger" title="Убрать товар" aria-label="Убрать товар"
                onClick={() => { setFromProduct(null); setShowFromSearch(false); }}>
                <TrashIcon />
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn-secondary btn-md btn-block" onClick={() => setShowFromSearch(true)}>
              Выбрать товар
            </button>
          )}
          {showFromSearch && !fromProduct && (
            <div style={{ marginTop: 8 }}>
              <ProductSearchDropdown
                onSelect={(p) => { setFromProduct(p); setShowFromSearch(false); }}
                excludeIds={excludeFromIds}
                onClose={() => setShowFromSearch(false)}
              />
            </div>
          )}
        </div>

        {/* Стрелка */}
        <div style={{ display: 'flex', alignItems: 'center', paddingTop: 28, fontSize: 28, color: '#999' }}>
          →
        </div>

        {/* Куда */}
        <div style={{ flex: 1 }}>
          <label className="form-label">Куда (на самом деле)</label>
          {toProduct ? (
            <div style={{
              border: '2px solid #359250', '--ch': '6px', '--stroke': '#359250', '--bw': '2px', padding: '10px 12px',
              background: '#EDFBEF', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{toProduct.name}</div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                  Физ. остаток: {toProduct.physical_quantity || 0}
                </div>
              </div>
              <button type="button" className="btn-icon is-danger" title="Убрать товар" aria-label="Убрать товар"
                onClick={() => { setToProduct(null); setShowToSearch(false); }}>
                <TrashIcon />
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn-secondary btn-md btn-block" onClick={() => setShowToSearch(true)}>
              Выбрать товар
            </button>
          )}
          {showToSearch && !toProduct && (
            <div style={{ marginTop: 8 }}>
              <ProductSearchDropdown
                onSelect={(p) => { setToProduct(p); setShowToSearch(false); }}
                excludeIds={excludeToIds}
                onClose={() => setShowToSearch(false)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Количество */}
      <div style={{ marginTop: 16, maxWidth: 200 }}>
        <label className="form-label">Количество</label>
        <input type="text" inputMode="numeric" className="form-input" value={quantity}
          onFocus={e => e.target.select()}
          onChange={e => { const v = e.target.value.replace(/\D/g, ''); setQuantity(v); }}
          onBlur={() => { if (!quantity || parseInt(quantity) < 1) setQuantity('1'); }}
          style={{ textAlign: 'center', fontSize: 16, fontWeight: 600 }}
        />
        {fromProduct && (
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
            Максимум: {fromProduct.quantity || 0}
          </div>
        )}
      </div>

      {/* Себестоимость */}
      <div style={{ marginTop: 16, maxWidth: 260 }}>
        <label className="form-label">Себестоимость, ₽/шт</label>
        <input type="text" inputMode="decimal" className="form-input" value={unitCost} placeholder="нет закупочной цены"
          onChange={e => setUnitCost(e.target.value.replace(/[^\d.,]/g, ''))} />
        <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
          Закупочная цена исходного товара — станет себестоимостью целевого в продажах, остатках и списаниях
        </div>
      </div>
    </Modal>
  );
}

function MisgradeViewModal({ misgrade, onClose }) {
  return (
    <Modal
      title={`Пересорт №${misgrade.id} от ${docDateTimeText(misgrade.date, misgrade.created_at)}`}
      size="large"
      onClose={onClose}
    >
      {misgrade.created_by_name && (
        <div style={{ marginBottom: 12, fontSize: 14 }}>
          <strong>Создал:</strong> {misgrade.created_by_name}
        </div>
      )}
      {misgrade.comment && (
        <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
          <strong>Комментарий:</strong> {misgrade.comment}
        </div>
      )}

      <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9F9F6' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Откуда</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 40 }}></th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Куда</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 80 }}>Кол-во</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, width: 120 }}>Себестоимость</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderTop: '1px solid #EBEDEC' }}>
              <td style={{ padding: '10px', color: '#CB4D45', fontWeight: 500 }}>
                {misgrade.from_product_name}
              </td>
              <td style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: 18 }}>→</td>
              <td style={{ padding: '10px', color: '#359250', fontWeight: 500 }}>
                {misgrade.to_product_name}
              </td>
              <td style={{ padding: '10px', textAlign: 'center', fontWeight: 600 }}>
                {misgrade.quantity}
              </td>
              <td style={{ padding: '10px', textAlign: 'right' }}>
                {misgrade.unit_cost != null ? `${Number(misgrade.unit_cost).toLocaleString('ru-RU')} ₽/шт` : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// ============================================================
// TAB: Инвентаризация (Inventory)
// ============================================================
function InventoryTab({ showToast, createTrigger, onCreateHandled }) {
  const [inventories, setInventories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInventory, setEditingInventory] = useState(null);
  const [viewInventory, setViewInventory] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '' });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getInventories(filters);
      setInventories(data?.items || []);
    } catch {
      showToast('Ошибка загрузки инвентаризаций', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  // Trigger из tab-strip кнопки
  useEffect(() => {
    if (createTrigger) {
      setShowEditModal(true);
      onCreateHandled && onCreateHandled();
    }
  }, [createTrigger, onCreateHandled]);

  // Period-фильтр в заголовке
  const [showDateFilter, setShowDateFilter] = useState(false);
  const dateFilterTriggerRef = useRef(null);
  const dateLabel = filters.dateFrom && filters.dateTo
    ? `${formatDate(filters.dateFrom).slice(0,5)} — ${formatDate(filters.dateTo).slice(0,5)}`
    : filters.dateFrom ? `с ${formatDate(filters.dateFrom)}`
    : filters.dateTo ? `до ${formatDate(filters.dateTo)}`
    : null;

  const handleRowClick = async (inv) => {
    try {
      const data = await api.getInventory(inv.id);
      if (inv.status === 'draft') {
        setEditingInventory(data);
      } else {
        setViewInventory(data);
      }
    } catch {
      showToast('Ошибка загрузки', 'error');
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      title: 'Удаление инвентаризации',
      message: 'Удалить черновик инвентаризации?',
      confirmText: 'Удалить',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.deleteInventory(id);
          showToast('Черновик удалён');
          loadData();
        } catch (error) {
          showToast(error.message || 'Ошибка удаления', 'error');
        }
      },
    });
  };

  const inventoryStatusLabel = { draft: 'Черновик', completed: 'Завершена' };
  const inventoryStatusBadge = { draft: 'badge-new', completed: 'badge-active' };

  return (
    <>
      {isLoading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="table-container" style={{ marginTop: 16 }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>ID</th>
                <th>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <button ref={dateFilterTriggerRef} type="button"
                      onClick={() => setShowDateFilter(v => !v)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: dateLabel ? '#1C3D2E' : 'inherit', fontWeight: dateLabel ? 700 : 'inherit', fontSize: 'inherit', fontFamily: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>
                      <span>{dateLabel || 'Дата'}</span>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </button>
                    {dateLabel && (
                      <button onClick={(e) => { e.stopPropagation(); setFilters(f => ({ ...f, dateFrom: '', dateTo: '' })); }}
                        title="Сбросить" style={{ width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%', cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                    )}
                  </span>
                  {showDateFilter && (
                    <DateRangeCalendar anchorEl={dateFilterTriggerRef}
                      dateFrom={filters.dateFrom} dateTo={filters.dateTo}
                      onSelect={(from, to) => { setFilters(f => ({ ...f, dateFrom: from, dateTo: to })); setShowDateFilter(false); }}
                      onClose={() => setShowDateFilter(false)} />
                  )}
                </th>
                <th>Статус</th>
                <th>Позиций</th>
                <th>Посчитано</th>
                <th>Расхождений</th>
                <th>Провёл</th>
                <th>Комментарий</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {inventories.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>
                    Инвентаризаций пока нет
                  </td>
                </tr>
              ) : inventories.map(inv => (
                <tr key={inv.id}
                  className="clickable-row"
                  onClick={() => handleRowClick(inv)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ padding: '8px 12px', color: '#5E6561', fontSize: 13, fontFamily: 'monospace' }}>#{inv.id}</td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}><DocDateTime date={inv.date} ts={inv.completed_at || inv.created_at} verb={inv.completed_at ? 'проведена' : 'создана'} /></td>
                  <td style={{ padding: '8px 12px' }}>
                    <span className={`badge ${inventoryStatusBadge[inv.status] || ''}`}>
                      {inventoryStatusLabel[inv.status] || inv.status}
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{inv.total_items}</td>
                  <td style={{ padding: '8px 12px' }}>{inv.counted_items}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {inv.total_differences > 0 && (
                      <span style={{
                        display: 'inline-block', padding: '1px 6px', '--ch': '6px',
                        fontSize: 11, fontWeight: 600,
                        background: '#FFE6E3', color: '#CB4D45',
                      }}>
                        {inv.total_differences}
                      </span>
                    )}
                    {inv.total_differences === 0 && <span style={{ color: '#999' }}>0</span>}
                  </td>
                  <td style={{ padding: '8px 12px' }}>{inv.created_by_name || '—'}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: '#666' }} title={inv.comment || ''}><div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.comment || '—'}</div></td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    {inv.status === 'draft' && (
                      <button
                        onClick={() => handleDelete(inv.id)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: 16, color: '#999', padding: '2px 6px', lineHeight: 1,
                        }}
                        title="Удалить черновик"
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create new inventory */}
      {showEditModal && (
        <InventoryEditModal
          onClose={() => setShowEditModal(false)}
          onSaved={() => { setShowEditModal(false); loadData(); }}
          showToast={showToast}
        />
      )}

      {/* Edit draft */}
      {editingInventory && (
        <InventoryEditModal
          inventory={editingInventory}
          onClose={() => setEditingInventory(null)}
          onSaved={() => { setEditingInventory(null); loadData(); }}
          showToast={showToast}
        />
      )}

      {/* View completed */}
      {viewInventory && (
        <InventoryViewModal
          inventory={viewInventory}
          onClose={() => setViewInventory(null)}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </>
  );
}

// ---- Inventory Edit Modal (create + edit draft) ----
function InventoryEditModal({ inventory, onClose, onSaved, showToast }) {
  const isEditing = !!inventory;
  const [comment, setComment] = useState(isEditing ? (inventory.comment || '') : '');
  const [items, setItems] = useState([]);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [allProducts, setAllProducts] = useState([]);

  // Один запрос каталога на модалку: и для дропдауна «+ Добавить товар» (physicalQtyMap),
  // и для предзаполнения листа пересчёта — раньше два эффекта грузили один и тот же список.
  useEffect(() => {
    if (isEditing) {
      setItems(inventory.items.map(it => ({
        product_id: it.product_id,
        product_name: it.product_name,
        expected_quantity: it.expected_quantity,
        actual_quantity: it.actual_quantity,
      })));
    }
    // limit=500 — как в дропдауне «+ Добавить товар»: активных товаров > 200,
    // и позиции из хвоста (sort_order, id DESC) не попадали в лист пересчёта.
    api.getProducts(1, 500).then(data => {
      const products = data.items || data;
      setAllProducts(products);
      if (!isEditing) {
        // New: грузим товары с «стеблями» > 0 (физика россыпью = physical − залок в букетах).
        // Считаем только россыпь; стебли в собранных букетах инвентаризируются отдельно.
        const looseOf = (p) => p.loose ?? Math.max(0, (p.physical_quantity || 0) - (p.bouquet_locked || 0));
        setItems(products.filter(p => looseOf(p) > 0).map(p => ({
          product_id: p.id,
          product_name: p.name,
          expected_quantity: looseOf(p),
          actual_quantity: null,
        })));
      }
    }).catch(() => {
      if (!isEditing) showToast('Ошибка загрузки товаров', 'error');
    });
  }, []);

  // Карта физ-остатков для дропдауна добавления товара —
  // показываем «реально на складе сейчас», без future_stock.
  const physicalQtyMap = useMemo(() => {
    const map = {};
    allProducts.forEach(p => { map[String(p.id)] = p.physical_quantity || 0; });
    return map;
  }, [allProducts]);

  const addProduct = (product) => {
    setItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      expected_quantity: 0,
      actual_quantity: null,
    }]);
    setShowProductSearch(false);
  };

  const updateActual = (idx, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, actual_quantity: value } : item));
  };

  const filteredItems = items.filter(it =>
    it.product_name.toLowerCase().includes(search.toLowerCase())
  );

  const countedItems = items.filter(it => it.actual_quantity !== null && it.actual_quantity !== '').length;
  const diffItems = items.filter(it => {
    if (it.actual_quantity === null || it.actual_quantity === '') return false;
    return (parseInt(it.actual_quantity) || 0) !== it.expected_quantity;
  }).length;

  const buildPayload = () => ({
    comment: comment || null,
    items: items.map(it => ({
      product_id: it.product_id,
      expected_quantity: it.expected_quantity,
      actual_quantity: it.actual_quantity === null || it.actual_quantity === '' ? null : parseInt(it.actual_quantity) || 0,
    })),
  });

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEditing) {
        await api.updateInventory(inventory.id, payload);
      } else {
        await api.createInventory(payload);
      }
      showToast('Инвентаризация сохранена');
      onSaved();
    } catch (error) {
      showToast(error.message || 'Ошибка сохранения', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = () => {
    setConfirmDialog({
      title: 'Завершить инвентаризацию',
      message: 'После завершения изменить будет нельзя. Разница будет применена к остаткам.',
      confirmText: 'Завершить',
      // не удаление — обычное подтверждение, без красного (владелец 25.10)
      onConfirm: async () => {
        setConfirmDialog(null);
        setSaving(true);
        try {
          const payload = buildPayload();
          let id = inventory?.id;
          if (isEditing) {
            await api.updateInventory(id, payload);
          } else {
            const created = await api.createInventory(payload);
            id = created.id;
          }
          await api.completeInventory(id);
          showToast('Инвентаризация завершена, остатки обновлены');
          onSaved();
        } catch (error) {
          showToast(error.message || 'Ошибка завершения', 'error');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  return (
    <Modal
      title={isEditing ? 'Редактирование инвентаризации' : 'Новая инвентаризация'}
      confirmOverlayClose
      size="xl"
      onClose={onClose}
      footer={
        <>
          {/* Одна главная — «Завершить», крайняя справа; черновик — контурная (UX-аудит 25.10) */}
          <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить черновик'}
          </button>
          <button className="btn btn-primary" onClick={handleComplete} disabled={saving}>
            Завершить инвентаризацию
          </button>
        </>
      }
    >
      <div className="form-group" style={{ marginBottom: 16 }}>
        <label className="form-label">Комментарий</label>
        <input type="text" className="form-input" placeholder="Необязательно"
          value={comment} onChange={e => setComment(e.target.value)} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <label className="form-label" style={{ margin: 0 }}>
            Позиции ({items.length})
          </label>
          <span style={{ fontSize: 12, color: '#666' }}>
            Посчитано: {countedItems} / {items.length}
            {diffItems > 0 && <span style={{ color: '#CB4D45', marginLeft: 8 }}>Расхождений: {diffItems}</span>}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="Поиск..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 13, border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', width: 150 }}
          />
          <button type="button" className="btn-link" onClick={() => setShowProductSearch(true)}>
            + Добавить товар
          </button>
        </div>
      </div>

      {showProductSearch && (
        <div style={{ marginBottom: 12 }}>
          <ProductSearchDropdown
            onSelect={addProduct}
            excludeIds={items.map(i => i.product_id)}
            onClose={() => setShowProductSearch(false)}
            maxQtyMap={physicalQtyMap}
            availabilityLabel="На складе"
          />
        </div>
      )}

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#999', padding: 20, fontSize: 13 }}>
          Загрузка товаров...
        </div>
      ) : (
        <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'hidden' }}>
          <div style={{ maxHeight: 450, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9F9F6', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Товар</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Система</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Факт</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Разница</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item, idx) => {
                  const realIdx = items.indexOf(item);
                  const actual = item.actual_quantity;
                  const hasValue = actual !== null && actual !== '';
                  const diff = hasValue ? (parseInt(actual) || 0) - item.expected_quantity : null;

                  return (
                    <tr key={item.product_id} style={{
                      borderTop: '1px solid #EBEDEC',
                      background: diff !== null && diff !== 0
                        ? (diff > 0 ? '#EDFBEF' : '#FFF4F0')
                        : '#fff'
                    }}>
                      <td style={{ padding: '8px 10px', fontWeight: 500 }}>{item.product_name}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#999' }}>
                        {item.expected_quantity}
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={hasValue ? actual : ''}
                          placeholder="—"
                          onFocus={e => e.target.select()}
                          onChange={e => {
                            const v = e.target.value.replace(/\D/g, '');
                            updateActual(realIdx, v === '' ? null : parseInt(v));
                          }}
                          style={{
                            width: '100%', padding: '4px 6px', textAlign: 'center',
                            border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13,
                            boxSizing: 'border-box',
                          }}
                        />
                      </td>
                      <td style={{
                        padding: '8px 10px', textAlign: 'center', fontWeight: 600,
                        color: diff === null ? '#ccc' : diff === 0 ? '#999' : diff > 0 ? '#359250' : '#CB4D45'
                      }}>
                        {diff === null ? '—' : diff === 0 ? '0' : (diff > 0 ? `+${diff}` : diff)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </Modal>
  );
}

// ---- Inventory View Modal (completed) ----
function InventoryViewModal({ inventory, onClose }) {
  return (
    <Modal
      title={`Инвентаризация №${inventory.id} от ${docDateTimeText(inventory.date, inventory.completed_at || inventory.created_at)}`}
      size="xl"
      onClose={onClose}
      footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Закрыть</button>}
    >
      <div style={{ display: 'flex', gap: 24, marginBottom: 16, fontSize: 14, flexWrap: 'wrap' }}>
        <div>
          <strong>Статус:</strong>{' '}
          <span className="badge badge-active">Завершена</span>
        </div>
        {inventory.completed_at && (
          <div><strong>Завершена:</strong> {formatDateTime(inventory.completed_at)}</div>
        )}
        {inventory.created_by_name && (
          <div><strong>Провёл:</strong> {inventory.created_by_name}</div>
        )}
      </div>
      {inventory.comment && (
        <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
          {inventory.comment}
        </div>
      )}

      <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
        Позиций: {inventory.total_items} · Посчитано: {inventory.counted_items} · Расхождений: {inventory.total_differences}
      </div>

      <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'hidden' }}>
        <div style={{ maxHeight: 450, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9F9F6', position: 'sticky', top: 0, zIndex: 1 }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Товар</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Система</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Факт</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Разница</th>
              </tr>
            </thead>
            <tbody>
              {inventory.items.map(item => {
                const diff = item.difference;
                const hasValue = item.actual_quantity !== null;

                return (
                  <tr key={item.product_id} style={{
                    borderTop: '1px solid #EBEDEC',
                    background: diff !== null && diff !== 0
                      ? (diff > 0 ? '#EDFBEF' : '#FFF4F0')
                      : '#fff'
                  }}>
                    <td style={{ padding: '8px 10px', fontWeight: 500 }}>{item.product_name}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'center', color: '#999' }}>
                      {item.expected_quantity}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 500 }}>
                      {hasValue ? item.actual_quantity : '—'}
                    </td>
                    <td style={{
                      padding: '8px 10px', textAlign: 'center', fontWeight: 600,
                      color: diff === null ? '#ccc' : diff === 0 ? '#999' : diff > 0 ? '#359250' : '#CB4D45'
                    }}>
                      {diff === null ? '—' : diff === 0 ? '0' : (diff > 0 ? `+${diff}` : diff)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// TAB: История движений (Movements)
// ============================================================
function MovementsTab({ showToast }) {
  const [movements, setMovements] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // type фильтр убран — никто не пользуется
  const [filters, setFilters] = useState({ productId: '', dateFrom: '', dateTo: '' });
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Period-фильтр в заголовке
  const [showDateFilter, setShowDateFilter] = useState(false);
  const dateFilterTriggerRef = useRef(null);
  const dateLabel = filters.dateFrom && filters.dateTo
    ? `${formatDate(filters.dateFrom).slice(0,5)} — ${formatDate(filters.dateTo).slice(0,5)}`
    : filters.dateFrom ? `с ${formatDate(filters.dateFrom)}`
    : filters.dateTo ? `до ${formatDate(filters.dateTo)}`
    : null;

  // Product search filter
  const [allProducts, setAllProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productSearchRef = useRef(null);

  useEffect(() => {
    // Все товары (активные + архивные) — движения есть и по архивным стеблям.
    api.getProductsAll(true).then(data => setAllProducts(data.items || data)).catch(() => {});
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (productSearchRef.current && !productSearchRef.current.contains(e.target)) {
        setShowProductDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Модалки источника движения (по клику на тип-бейдж)
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [viewWriteOff, setViewWriteOff] = useState(null);
  const [viewMisgrade, setViewMisgrade] = useState(null);
  const [viewInventory, setViewInventory] = useState(null);

  const openMovementSource = useCallback(async (m) => {
    if (!m.reference_type || !m.reference_id) return;
    try {
      if (m.reference_type === 'invoice') {
        const data = await api.getInvoice(m.reference_id);
        setViewInvoice(data);
      } else if (m.reference_type === 'order') {
        const data = await api.getOrderById(m.reference_id);
        setViewOrder(data);
      } else if (m.reference_type === 'write_off') {
        const data = await api.getWriteOff(m.reference_id);
        setViewWriteOff(data);
      } else if (m.reference_type === 'misgrade') {
        const data = await api.getMisgrade(m.reference_id);
        setViewMisgrade(data);
      } else if (m.reference_type === 'inventory') {
        const data = await api.getInventory(m.reference_id);
        setViewInventory(data);
      }
    } catch {
      showToast('Не удалось открыть карточку — объект мог быть удалён', 'error');
    }
  }, [showToast]);

  const openInvoiceById = useCallback(async (invoiceId) => {
    try {
      const data = await api.getInvoice(invoiceId);
      setViewInvoice(data);
    } catch {
      showToast('Накладная не найдена', 'error');
    }
  }, [showToast]);

  const openOrderById = useCallback(async (orderId) => {
    try {
      const data = await api.getOrderById(orderId);
      setViewOrder(data);
    } catch {
      showToast('Заказ не найден', 'error');
    }
  }, [showToast]);

  const openWriteOffById = useCallback(async (id) => {
    try { setViewWriteOff(await api.getWriteOff(id)); }
    catch { showToast('Списание не найдено', 'error'); }
  }, [showToast]);

  const openInventoryById = useCallback(async (id) => {
    try { setViewInventory(await api.getInventory(id)); }
    catch { showToast('Инвентаризация не найдена', 'error'); }
  }, [showToast]);

  const openMisgradeById = useCallback(async (id) => {
    try { setViewMisgrade(await api.getMisgrade(id)); }
    catch { showToast('Пересорт не найден', 'error'); }
  }, [showToast]);

  const filteredProducts = allProducts
    .filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()));

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getStockMovements({ ...filters, limit, offset });
      setMovements(data.items);
      setTotal(data.total);
    } catch {
      showToast('Ошибка загрузки движений', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [filters, offset, showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFilterChange = (field, value) => {
    setOffset(0);
    setFilters(f => ({ ...f, [field]: value }));
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const [showLegend, setShowLegend] = useState(false);

  // Inline-стиль для <code> в гайде «Как читать историю» — бренд-нейтральный фон.
  const legendCode = {
    background: '#F4F4EF',
    padding: '1px 6px',
    borderRadius: 3,
    fontSize: '0.92em',
    fontFamily: 'inherit',
    color: '#1C3D2E',
  };

  return (
    <>
      <div className="card" style={{
        marginTop: 16, padding: 14,
        background: '#FFFFFF', border: '1px solid #EBEDEC', '--ch': '8px', '--stroke': '#EBEDEC',
      }}>
        <button
          onClick={() => setShowLegend(s => !s)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: 600, color: '#1C3D2E',
            padding: 0, display: 'flex', alignItems: 'center', gap: 8,
            fontFamily: 'inherit',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
               style={{ transform: showLegend ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
            <polyline points="9 18 15 12 9 6" />
          </svg>
          Как читать историю движений
        </button>
        {showLegend && (
          <div style={{
            marginTop: 14, fontSize: 13, color: '#1C3D2E', lineHeight: 1.55,
            fontFamily: 'inherit',
          }}>
            {/* Колонки таблицы */}
            <div style={{
              fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
              color: '#5E6561', letterSpacing: 0.4, marginBottom: 8,
            }}>Колонки таблицы</div>
            <ul style={{ margin: '0 0 16px 0', paddingLeft: 18, listStyle: 'disc' }}>
              <li><b>Кол-во</b> — изменение количества движением. Зелёное — приход, красное — расход.</li>
              <li><b>Физ. остаток</b> — фактический физический склад после движения. <code style={legendCode}>(15)</code> в скобках серым — справочно: движение НЕ меняло физ.склад (резерв/дефицит/перенос). Без скобок <code style={legendCode}>15</code> — реальное изменение.</li>
              <li><b>Доступно</b> = физ.склад − активные резервации в окне свежести. Может уменьшаться без изменения физ.остатка — значит кто-то оформил заказ.</li>
              <li><b>Дефицит</b> — открытый product-уровневый дефицит после движения. Распределяется по заказам через FIFO (самые поздние получают дефицит). <code style={legendCode}>0</code> — дефицит закрыт.</li>
              <li><b>Тип</b> — кликабельный бейдж: открывает источник (накладную, заказ, списание, инвентаризацию, пересорт).</li>
              <li><b>Комментарий</b> — содержит кликабельные ссылки: упоминания «накладная X», «№заказа», «Списание №N», «Инвентаризация №N», «Пересорт №N» открывают карточку соответствующего объекта.</li>
            </ul>

            {/* Что меняет физ.остаток */}
            <div style={{
              fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
              color: '#5E6561', letterSpacing: 0.4, marginBottom: 8,
            }}>Что меняет физический остаток</div>
            <ul style={{ margin: '0 0 16px 0', paddingLeft: 18, listStyle: 'disc' }}>
              <li><b>Прирост:</b> приход накладной, отмена/возврат заказа, инвентаризация (+), пересорт (приход в целевой товар).</li>
              <li><b>Расход:</b> завершение заказа, списание, брак при приёмке, инвентаризация (−), пересорт (расход из исходного товара).</li>
              <li><i>Создание заказа физ.остаток НЕ меняет</i> — только уменьшает «Доступно» через резервацию. Физический сток списывается в момент <i>Завершения</i>.</li>
            </ul>

            {/* Жизненный цикл заказа */}
            <div style={{
              fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
              color: '#5E6561', letterSpacing: 0.4, marginBottom: 8,
            }}>Жизненный цикл заказа в истории</div>
            <ul style={{ margin: '0 0 16px 0', paddingLeft: 18, listStyle: 'disc' }}>
              <li><b>Создание заказа</b> оставляет один из следов:
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                  <li><i>Продажа</i> — резерв с текущего физ.склада.</li>
                  <li><i>Резерв из плановой накладной</i> — зарезервировано на будущей поставке (физ.склад не трогает).</li>
                  <li><i>Заказ-долг</i> — нет ни физ.остатка, ни плановой → дефицит, ждёт поставку.</li>
                </ul>
              </li>
              <li><b>Редактирование</b> заказа (смена состава/количества/даты): эмитит <i>Редактирование</i> + при смене источника резерва <i>Освобождение из накладной</i> + <i>Резерв из накладной</i> с пометкой «(редактирование заказа)».</li>
              <li><b>Завершение</b> списывает физ.остаток и закрывает остаточный дефицит этого заказа (если он был).</li>
              <li><b>Отмена</b> возвращает резервы (плановые освобождает, физические возвращает на склад) и закрывает остаточный дефицит.</li>
            </ul>

            {/* Дефицит */}
            <div style={{
              fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
              color: '#5E6561', letterSpacing: 0.4, marginBottom: 8,
            }}>Дефицит</div>
            <ul style={{ margin: '0 0 16px 0', paddingLeft: 18, listStyle: 'disc' }}>
              <li><b>Возникает</b> только при создании заказа без покрытия или при смене даты доставки заказа на дату вне окон существующих стоков.</li>
              <li><b>Закрывается</b> приёмкой накладной (видно в комменте к Приходу: «Покрытие дефицитов: №X (Y/Z шт)»), созданием плановой накладной с покрывающей датой, отменой/завершением заказа.</li>
              <li><b>FIFO-перераспределение</b> — при изменениях стока система пересчитывает «кому именно из заказов не хватит» по дате доставки (ранние получают физ. первыми). В истории видно как <i>FIFO-коррекция</i>: дефицит «переезжает» с одного заказа на другой.</li>
              <li><b>«Корректировка истории дефицита»</b> — служебная запись для приведения журнала к балансу 0 (защита от artefact-ов). Реального товара не появляется/не пропадает.</li>
            </ul>

            {/* Упаковка / автоматика */}
            <div style={{
              fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
              color: '#5E6561', letterSpacing: 0.4, marginBottom: 8,
            }}>Прочее</div>
            <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'disc' }}>
              <li><b>Перенос с текущего склада на плановую</b> — автоматическая оптимизация: при создании/приёмке плановой накладной система переписывает резервы заказов с физ.склада на плановую, если плановая успевает прийти к доставке заказа. Освобождает физ.склад под walk-in заказы.</li>
              <li><b>Промоция в плановую</b> — дефицитный заказ переезжает на свежесозданную плановую накладную, которая успевает к его дате.</li>
              <li><b>Снятие резерва из накладной</b> — обратное освобождение плановой при отмене заказа или редактировании в меньшую сторону.</li>
            </ul>
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="table-container" style={{ marginTop: 16 }}>
            <table className="table" style={{ tableLayout: 'fixed', width: '100%', minWidth: 1085 }}>
              <colgroup>
                <col style={{ width: 150 }} />{/* Дата/время */}
                <col style={{ width: 200 }} />{/* Товар */}
                <col style={{ width: 240 }} />{/* Тип (объединён с инфой о поставке) */}
                <col style={{ width: 80 }} />{/* Кол-во */}
                <col style={{ width: 110 }} />{/* Физ. остаток */}
                <col style={{ width: 95 }} />{/* Доступно */}
                <col style={{ width: 90 }} />{/* Дефицит */}
                <col />{/* Комментарий — flex */}
              </colgroup>
              <thead>
                <tr>
                  <th>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <button ref={dateFilterTriggerRef} type="button"
                        onClick={() => setShowDateFilter(v => !v)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: dateLabel ? '#1C3D2E' : 'inherit', fontWeight: dateLabel ? 700 : 'inherit', fontSize: 'inherit', fontFamily: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>
                        <span>{dateLabel || 'Дата/время'}</span>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                      </button>
                      {dateLabel && (
                        <button onClick={(e) => { e.stopPropagation(); setOffset(0); setFilters(f => ({ ...f, dateFrom: '', dateTo: '' })); }}
                          title="Сбросить" style={{ width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%', cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      )}
                    </span>
                    {showDateFilter && (
                      <DateRangeCalendar anchorEl={dateFilterTriggerRef}
                        dateFrom={filters.dateFrom} dateTo={filters.dateTo}
                        onSelect={(from, to) => { setOffset(0); setFilters(f => ({ ...f, dateFrom: from, dateTo: to })); setShowDateFilter(false); }}
                        onClose={() => setShowDateFilter(false)} />
                    )}
                  </th>
                  <th ref={productSearchRef} style={{ position: 'relative', width: 200, minWidth: 200, maxWidth: 200 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
                      <button type="button"
                        onClick={() => setShowProductDropdown(v => !v)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: selectedProduct ? '#1C3D2E' : 'inherit', fontWeight: selectedProduct ? 700 : 'inherit', fontSize: 'inherit', fontFamily: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', minWidth: 0, maxWidth: '100%' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{selectedProduct ? selectedProduct.name : 'Товар'}</span>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                      </button>
                      {selectedProduct && (
                        <button onClick={(e) => { e.stopPropagation(); setSelectedProduct(null); setProductSearch(''); handleFilterChange('productId', ''); }}
                          title="Сбросить" style={{ width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%', cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      )}
                    </span>
                    {showProductDropdown && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1500, background: '#fff', border: '1px solid #D8DDDA', '--ch': '8px', '--stroke': '#D8DDDA', boxShadow: '0 8px 24px rgba(28,61,46,0.12)', width: 280, marginTop: 4, padding: 6, textTransform: 'none', letterSpacing: 0 }}>
                        <input autoFocus type="text" placeholder="Поиск товара…"
                          value={productSearch} onChange={e => setProductSearch(e.target.value)}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #D8DDDA', '--ch': '5px', '--stroke': '#D8DDDA', fontSize: 13, marginBottom: 4, outline: 'none' }} />
                        <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                          {filteredProducts.length === 0 ? (
                            <div style={{ padding: 8, fontSize: 13, color: '#999', textAlign: 'center' }}>Не найдено</div>
                          ) : filteredProducts.slice(0, 8).map(p => (
                            <div key={p.id}
                              onClick={() => {
                                setSelectedProduct(p);
                                setProductSearch('');
                                setShowProductDropdown(false);
                                handleFilterChange('productId', p.id);
                              }}
                              style={{ padding: '7px 10px', fontSize: 13, cursor: 'pointer', '--ch': '5px', fontWeight: 500, color: p.is_active === false ? '#909894' : 'inherit' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#F9F9F6'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              {p.name}{p.is_active === false ? ' · архив' : ''}
                            </div>
                          ))}
                          {filteredProducts.length > 8 && (
                            <div style={{ padding: '7px 10px', fontSize: 12, color: '#909894', textAlign: 'center' }}>
                              Показаны первые 8. Уточните запрос.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </th>
                  <th>Тип</th>
                  <th>Кол-во</th>
                  <th>Физ. остаток</th>
                  <th>Доступно</th>
                  <th>Дефицит</th>
                  <th>Комментарий</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>
                      Движений пока нет
                    </td>
                  </tr>
                ) : movements.map(m => (
                  <tr key={m.id}>
                    <td style={{ padding: '8px 12px', fontSize: 13, whiteSpace: 'nowrap' }}>
                      {formatDateTime(m.created_at)}
                    </td>
                    <td style={{ padding: '8px 12px', width: 200, minWidth: 200, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.product_name}>{m.product_name}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span
                        onClick={() => openMovementSource(m)}
                        style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '3px 9px', '--ch': '5px',
                          fontSize: 12, fontWeight: 600, lineHeight: 1.3,
                          background: `${movementTypeColor[m.type] || '#999'}20`,
                          color: movementTypeColor[m.type] || '#999',
                          cursor: m.reference_id ? 'pointer' : 'default',
                          userSelect: 'none',
                        }}
                        title={m.reference_id ? 'Открыть карточку' : ''}
                        onMouseEnter={e => { if (m.reference_id) e.currentTarget.style.filter = 'brightness(0.92)'; }}
                        onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
                      >
                        {m.type === 'receipt'
                          ? `${m.is_from_planned ? 'Плановая поставка' : 'Приходная'} ${m.invoice_number ? '№' + m.invoice_number : 'ID:' + m.reference_id}`
                          : m.type === 'subscription_fill' && m.order_id
                          ? `Наполнение: №${m.order_id}`
                          : m.type === 'sale' && m.order_id
                          ? `Продажа: №${m.order_id}`
                          : m.type === 'cancellation_return' && m.order_id
                            ? `Отмена: №${m.order_id}`
                            : m.type === 'completion' && m.order_id
                              ? `Завершение: №${m.order_id}`
                              : m.type === 'edit_return' && m.order_id
                                ? `Редактирование: №${m.order_id}`
                                : m.type === 'edit_sale' && m.order_id
                                  ? `Редактирование: №${m.order_id}`
                                  : m.type === 'reserve_planned' && m.order_id
                                    ? `Резерв: №${m.order_id}`
                                  : m.type === 'reserve_transfer_planned' && m.order_id
                                    ? `Перенос: №${m.order_id}`
                                  : m.type === 'reserve_back_to_current' && m.order_id
                                    ? `Возврат: №${m.order_id}`
                                    : m.type === 'deficit_create' && m.order_id
                                      ? `Заказ-долг: №${m.order_id}`
                                      : m.type === 'deficit_cover' && m.order_id
                                        ? `Покрытие: №${m.order_id}`
                                        : m.type === 'reserve_release_planned' && m.order_id
                                          ? `Снятие резерва: №${m.order_id}`
                                          : m.type === 'reserve_to_physical'
                                            ? `Перенос план→склад`
                                          : m.type === 'bouquet_completion' && m.order_id
                                            ? `Завершение №${m.order_id} • Букет`
                                          : m.type === 'bouquet_cancellation_return' && m.order_id
                                            ? `Возврат №${m.order_id} • Букет`
                                          : m.type === 'write_off' && m.reference_id
                                            ? `Списание: №${m.reference_id}`
                                          : m.type === 'transfer_out' && m.reference_id
                                            ? `Перемещение: №${m.reference_id}`
                                          : m.type === 'misgrade' && m.reference_id
                                            ? `Пересорт: №${m.reference_id}`
                                          : m.type === 'inventory' && m.reference_id
                                            ? `Инвентаризация: №${m.reference_id}`
                                            : (movementTypeLabel[m.type] || m.type)}
                      </span>
                    </td>
                    <td style={{
                      padding: '8px 12px', fontWeight: 600,
                      color: m.quantity > 0 ? '#359250' : '#CB4D45'
                    }}>
                      {m.quantity > 0 ? '+' : ''}{m.quantity}
                    </td>
                    <td style={{
                      padding: '8px 12px', fontSize: 13,
                      ...(LOGICAL_MOVEMENT_TYPES.has(m.type)
                        ? { color: '#bbb', fontStyle: 'italic', fontWeight: 400 }
                        : { color: '#222', fontWeight: 500 }),
                    }}
                    title={LOGICAL_MOVEMENT_TYPES.has(m.type) ? 'Физ. остаток не менялся (только резерв)' : undefined}>
                      {m.stock_after != null
                        ? (LOGICAL_MOVEMENT_TYPES.has(m.type) ? `(${m.stock_after})` : m.stock_after)
                        : '—'}
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 500, fontSize: 13 }}>
                      {m.available_after != null ? m.available_after : '—'}
                    </td>
                    <td style={{
                      padding: '8px 12px', fontSize: 13,
                      ...(m.deficit_outstanding != null && m.deficit_outstanding > 0
                        ? { color: '#9A4820', fontWeight: 600 }
                        : m.deficit_outstanding === 0
                          ? { color: '#999', fontWeight: 500 }
                          : { color: '#ccc' }),
                    }}>
                      {m.deficit_outstanding != null ? m.deficit_outstanding : '—'}
                      {/* Backend clampит deficit_outstanding к 0+ — отрицательное не появится. */}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 13, color: '#666', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                      <CommentWithRefs
                        comment={m.comment}
                        invoiceLabel={m.related_invoice_label}
                        invoiceId={m.related_invoice_id}
                        orders={m.related_orders}
                        sourceType={m.related_source_type}
                        sourceId={m.related_source_id}
                        onOpenInvoice={openInvoiceById}
                        onOpenOrder={openOrderById}
                        onOpenWriteOff={openWriteOffById}
                        onOpenInventory={openInventoryById}
                        onOpenMisgrade={openMisgradeById}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16, alignItems: 'center' }}>
              <button className="btn btn-sm btn-secondary"
                disabled={offset === 0}
                onClick={() => setOffset(o => Math.max(0, o - limit))}
              >
                Назад
              </button>
              <span style={{ fontSize: 13, color: '#666' }}>
                {currentPage} из {totalPages} ({total} записей)
              </span>
              <button className="btn btn-sm btn-secondary"
                disabled={offset + limit >= total}
                onClick={() => setOffset(o => o + limit)}
              >
                Вперёд
              </button>
            </div>
          )}
        </>
      )}
      {/* Модалки источника движения (открываются по клику на тип-бейдж) */}
      {viewInvoice && (
        <InvoiceViewModal invoice={viewInvoice} onClose={() => setViewInvoice(null)} />
      )}
      {viewOrder && (
        <OrderModal initialOrder={viewOrder} onClose={() => setViewOrder(null)} showToast={showToast} />
      )}
      {viewWriteOff && (
        <WriteOffViewModal writeOff={viewWriteOff} onClose={() => setViewWriteOff(null)} />
      )}
      {viewMisgrade && (
        <MisgradeViewModal misgrade={viewMisgrade} onClose={() => setViewMisgrade(null)} />
      )}
      {viewInventory && (
        <InventoryViewModal inventory={viewInventory} onClose={() => setViewInventory(null)} />
      )}
    </>
  );
}

// ============================================================
// Helpers
// ============================================================
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return dateStr;
}

// Дата документа + время, когда его внесли/провели. Дату выбирают в форме (можно задним
// числом), время не вводят — берётся из отметки сервера (created_at / accepted_at /
// completed_at). Внесли в тот же день — «07.10.2026 14:32»; в другой — дата и под ней
// серым «внесено 08.10 09:15», чтобы время не приписывалось чужому дню.
const pad2 = (n) => String(n).padStart(2, '0');
function tsParts(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return {
    iso: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    dm: `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`,
    hm: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  };
}

function DocDateTime({ date, ts, verb = 'внесено' }) {
  const t = tsParts(ts);
  const day = date || t?.iso;
  if (!t) return formatDate(day);
  if (t.iso === day) {
    return <span style={{ whiteSpace: 'nowrap' }}>{formatDate(day)} <span style={{ color: '#909894' }}>{t.hm}</span></span>;
  }
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {formatDate(day)}
      <span style={{ display: 'block', fontSize: 11, color: '#909894' }}>{verb} {t.dm} {t.hm}</span>
    </span>
  );
}

// То же строкой — для заголовков карточек «Списание №12 от 07.10.2026 14:32»
function docDateTimeText(date, ts) {
  const t = tsParts(ts);
  const day = date || t?.iso;
  return t && t.iso === day ? `${formatDate(day)} ${t.hm}` : formatDate(day);
}

function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString('ru-RU') + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoStr;
  }
}

// ============================================================================
// Инвентаризация СОБРАННЫХ букетов (TASK_BOUQUET_INVENTORY)
// Считаем собранные экземпляры по партиям. Недостача → утиль/разобрать,
// излишек → оприходование найденного. Стебли в букетах ≠ стебли россыпью.
// ============================================================================

function BouquetInventoryTab({ showToast, createTrigger, onCreateHandled }) {
  const [inventories, setInventories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [view, setView] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try { setInventories((await api.getBouquetInventories())?.items || []); }
    catch { showToast('Ошибка загрузки инвентаризаций букетов', 'error'); }
    finally { setIsLoading(false); }
  }, [showToast]);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (createTrigger) { setEditing(null); setShowModal(true); onCreateHandled && onCreateHandled(); }
  }, [createTrigger, onCreateHandled]);

  const statusLabel = { draft: 'Черновик', completed: 'Завершена' };
  const statusBadge = { draft: 'badge-new', completed: 'badge-active' };

  const openRow = async (inv) => {
    try {
      const data = await api.getBouquetInventory(inv.id);
      if (inv.status === 'draft') { setEditing(data); setShowModal(true); }
      else setView(data);
    } catch { showToast('Ошибка загрузки', 'error'); }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      title: 'Удаление инвентаризации', message: 'Удалить черновик инвентаризации букетов?',
      confirmText: 'Удалить', variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(null);
        try { await api.deleteBouquetInventory(id); showToast('Черновик удалён'); loadData(); }
        catch (e) { showToast(e.message || 'Ошибка удаления', 'error'); }
      },
    });
  };

  return (
    <>
      {isLoading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="table-container" style={{ marginTop: 16 }}>
          <table className="table">
            <thead><tr>
              <th style={{ width: 70 }}>ID</th><th>Дата</th><th>Статус</th><th>Позиций</th>
              <th>Посчитано</th><th>Расхождений</th><th>Провёл</th><th>Комментарий</th><th style={{ width: 40 }}></th>
            </tr></thead>
            <tbody>
              {inventories.length === 0 ? (
                <tr><td colSpan="9" style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>Нет инвентаризаций букетов</td></tr>
              ) : inventories.map(inv => (
                <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => openRow(inv)}>
                  <td>#{inv.id}</td>
                  <td><DocDateTime date={inv.date} ts={inv.completed_at || inv.created_at} verb={inv.completed_at ? 'проведена' : 'создана'} /></td>
                  <td><span className={`badge ${statusBadge[inv.status]}`}>{statusLabel[inv.status]}</span></td>
                  <td>{inv.total_items}</td>
                  <td>{inv.counted_items}</td>
                  <td>{inv.total_differences}</td>
                  <td>{inv.created_by_name || '—'}</td>
                  <td title={inv.comment || ''}><div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.comment || '—'}</div></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {inv.status === 'draft' && (
                      <button title="Удалить" onClick={() => handleDelete(inv.id)}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#B0603C', fontSize: 15 }}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showModal && (
        <BouquetInventoryEditModal inventory={editing} showToast={showToast}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={() => { setShowModal(false); setEditing(null); loadData(); }} />
      )}
      {view && <BouquetInventoryViewModal inventory={view} onClose={() => setView(null)} />}
      {confirmDialog && <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog(null)} />}
    </>
  );
}

const _todayLocal = () => todayMSKString(); // YYYY-MM-DD по МСК (как сервер)

function BouquetInventoryEditModal({ inventory, onClose, onSaved, showToast }) {
  const isEditing = !!(inventory && inventory.id);
  const [rows, setRows] = useState([]);
  const [comment, setComment] = useState(inventory?.comment || '');
  const [allBouquets, setAllBouquets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [completeConfirm, setCompleteConfirm] = useState(null); // C4: сводка перед завершением
  // форма «найденного букета»
  const [foundBid, setFoundBid] = useState('');
  const [foundQty, setFoundQty] = useState('');
  const [foundDate, setFoundDate] = useState(_todayLocal());
  const [foundShelf, setFoundShelf] = useState('');

  useEffect(() => {
    api.getBouquetsAdmin(false).then(d => setAllBouquets(d.items || d || [])).catch(() => {});
    if (isEditing) {
      setRows((inventory.items || []).map(it => ({
        key: `e${it.id}`,
        bouquet_id: it.bouquet_id, bouquet_name: it.bouquet_name,
        lot_id: it.lot_id, lot_assembled_at: it.lot_assembled_at, lot_shelf: it.lot_shelf_life_days,
        expected: it.expected_quantity, actual: it.actual_quantity ?? '',
        write_off_mode: it.write_off_mode || 'scrap',
        found: !it.lot_id,
        found_assembled_at: it.lot_assembled_at || _todayLocal(), found_shelf: '',
      })));
    } else {
      api.getBouquetInventoryCandidates().then(cands => {
        setRows((cands || []).map((c, i) => ({
          key: `c${c.lot_id}_${i}`,
          bouquet_id: c.bouquet_id, bouquet_name: c.bouquet_name,
          lot_id: c.lot_id, lot_assembled_at: c.assembled_at, lot_shelf: c.shelf_life_days,
          expected: c.expected_quantity, actual: '',
          write_off_mode: 'scrap', found: false,
        })));
      }).catch(() => showToast('Ошибка загрузки партий', 'error'));
    }
  }, []); // eslint-disable-line

  const updateRow = (key, patch) => setRows(prev => prev.map(r => r.key === key ? { ...r, ...patch } : r));
  const removeRow = (key) => setRows(prev => prev.filter(r => r.key !== key));

  const addFound = () => {
    if (!foundBid || !foundQty) { showToast('Выберите букет и количество', 'error'); return; }
    const b = allBouquets.find(x => String(x.id) === String(foundBid));
    setRows(prev => [...prev, {
      key: `f${foundBid}_${Date.now()}`,
      bouquet_id: parseInt(foundBid), bouquet_name: b ? b.name : `#${foundBid}`,
      lot_id: null, lot_assembled_at: null, lot_shelf: null,
      expected: 0, actual: String(parseInt(foundQty) || 0),
      write_off_mode: 'scrap', found: true,
      found_assembled_at: foundDate, found_shelf: foundShelf,
    }]);
    setFoundBid(''); setFoundQty(''); setFoundShelf(''); setFoundDate(_todayLocal());
  };

  const buildPayload = (rowsArg = rows) => ({
    comment: comment || null,
    // C3: строки с исчезнувшей партией (lot_gone) пропускаем — списывать/оприходовать
    // на несуществующую партию двусмысленно; они только подсвечиваются бейджем.
    items: rowsArg.filter(r => !r.lot_gone).map(r => ({
      bouquet_id: r.bouquet_id,
      lot_id: r.found ? null : r.lot_id,
      expected_quantity: r.expected,
      actual_quantity: (r.actual === '' || r.actual == null) ? null : parseInt(r.actual) || 0,
      // write_off_mode пишем только при реальной недостаче (факт < система), иначе null —
      // чтобы в карточке/данных не было ложного «Утиль» на строках без списания.
      write_off_mode: (() => {
        if (r.found) return null;
        const a = (r.actual === '' || r.actual == null) ? null : parseInt(r.actual) || 0;
        return (a != null && a < r.expected) ? 'scrap' : null;
      })(),
      returned_components: null, // v1: «разобрать» = полный возврат; частичный — позже
      found_assembled_at: r.found ? (r.found_assembled_at || _todayLocal()) : null,
      found_shelf_life_days: r.found ? (parseInt(r.found_shelf) || null) : null,
    })),
  });

  const persist = async (rowsArg) => {
    const payload = buildPayload(rowsArg);
    if (isEditing) { await api.updateBouquetInventory(inventory.id, payload); return inventory.id; }
    const created = await api.createBouquetInventory(payload);
    return created.id;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try { await persist(); showToast('Черновик сохранён'); onSaved(); }
    catch (e) { showToast(e.message || 'Ошибка сохранения', 'error'); }
    finally { setSaving(false); }
  };

  // C3: перед завершением заново спрашиваем у базы актуальные остатки партий —
  // черновик мог повисеть, и партии за это время изменились/исчезли.
  const refreshExpected = async () => {
    let candidates = [];
    try { candidates = await api.getBouquetInventoryCandidates() || []; }
    catch { return rows; } // не вышло — продолжаем со старыми числами (не блокируем)
    const candByLot = {};
    candidates.forEach(c => { candByLot[c.lot_id] = c; });
    return rows.map(r => {
      if (r.found || !r.lot_id) return { ...r, lot_gone: false, expected_changed: false };
      const cand = candByLot[r.lot_id];
      if (!cand) return { ...r, lot_gone: true, expected_changed: false, expected_prev: r.expected, expected: 0 };
      const fresh = cand.expected_quantity || 0;
      return { ...r, lot_gone: false, expected_changed: fresh !== r.expected, expected_prev: r.expected, expected: fresh };
    });
  };

  // C4: сводка «что сейчас произойдёт» из обновлённых строк.
  const buildCompleteSummary = (rs) => {
    const writeOffs = [], founds = [], gone = [];
    rs.forEach(r => {
      if (r.lot_gone) { gone.push(r); return; }
      const a = (r.actual === '' || r.actual == null) ? null : parseInt(r.actual) || 0;
      if (a == null) return;
      const diff = a - r.expected;
      if (diff < 0) writeOffs.push({ name: r.bouquet_name, n: -diff });
      else if (diff > 0) founds.push({ name: r.bouquet_name, n: diff });
    });
    if (!writeOffs.length && !founds.length && !gone.length) {
      return <span>Расхождений нет — остатки не изменятся, инвентаризация просто закроется.</span>;
    }
    const Sec = ({ color, title, items, render }) => (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 700, color, marginBottom: 3 }}>{title}</div>
        {items.map((it, i) => <div key={i} style={{ paddingLeft: 8 }}>• {render(it)}</div>)}
      </div>
    );
    return (
      <div>
        {writeOffs.length > 0 && <Sec color="#CB4D45" title={`Списать (утиль): ${writeOffs.reduce((s, x) => s + x.n, 0)} шт`}
          items={writeOffs} render={x => `${x.name} — ${x.n} шт`} />}
        {founds.length > 0 && <Sec color="#1F763B" title={`Оприходовать (найдено): ${founds.reduce((s, x) => s + x.n, 0)} шт`}
          items={founds} render={x => `${x.name} — ${x.n} шт`} />}
        {gone.length > 0 && <Sec color="#7E5D00" title="Пропущено (партии больше нет)"
          items={gone} render={x => `${x.bouquet_name} — партия исчезла, строка не применится`} />}
        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Действие необратимо.</div>
      </div>
    );
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const refreshed = await refreshExpected();
      setRows(refreshed); // показать свежую «Систему» + бейджи в таблице
      setCompleteConfirm({ summary: buildCompleteSummary(refreshed), rows: refreshed });
    } catch (e) { showToast(e.message || 'Ошибка', 'error'); }
    finally { setSaving(false); }
  };

  const doComplete = async (refreshed) => {
    setCompleteConfirm(null);
    setSaving(true);
    try {
      const id = await persist(refreshed);
      const result = await api.completeBouquetInventory(id);
      const def = result.deficit_summary || [];
      if (def.length) {
        const msg = def.map(d => `#${d.order_number || d.order_id} (${d.bouquet_name} ×${d.quantity})`).join(', ');
        showToast(`Завершено. Дефицит у заказов: ${msg}`, 'error');
      } else {
        showToast('Инвентаризация букетов завершена');
      }
      onSaved();
    } catch (e) { showToast(e.message || 'Ошибка завершения', 'error'); }
    finally { setSaving(false); }
  };

  const counted = rows.filter(r => r.actual !== '' && r.actual != null).length;
  const diffCount = rows.filter(r => {
    const a = (r.actual === '' || r.actual == null) ? null : parseInt(r.actual) || 0;
    return a != null && a !== r.expected;
  }).length;
  // Одна главная — «Завершить», крайняя справа; черновик — контурная (UX-аудит 25.10)
  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
      <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={saving}>
        {saving ? 'Сохранение...' : 'Сохранить черновик'}
      </button>
      <button className="btn btn-primary" onClick={handleComplete} disabled={saving}>
        Завершить инвентаризацию
      </button>
    </>
  );

  return (
    <>
    <Modal title={isEditing ? 'Редактирование инвентаризации букетов' : 'Новая инвентаризация букетов'}
      size="xl" onClose={onClose} confirmOverlayClose footer={footer}>
      <div className="form-group" style={{ marginBottom: 16 }}>
        <label className="form-label">Комментарий</label>
        <input type="text" className="form-input" placeholder="Необязательно"
          value={comment} onChange={(e) => setComment(e.target.value)} />
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12 }}>
        <label className="form-label" style={{ margin: 0 }}>Позиции ({rows.length})</label>
        <span style={{ fontSize: 12, color: '#666' }}>
          Посчитано: {counted} / {rows.length}
          {diffCount > 0 && <span style={{ color: '#CB4D45', marginLeft: 8 }}>Расхождений: {diffCount}</span>}
        </span>
      </div>

      <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'hidden' }}>
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9F9F6', position: 'sticky', top: 0, zIndex: 1 }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Букет</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Партия</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Система</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Факт</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Разница</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', color: '#999', padding: 20 }}>Нет собранных партий</td></tr>
              ) : rows.map(r => {
                const actualNum = (r.actual === '' || r.actual == null) ? null : parseInt(r.actual) || 0;
                const diff = actualNum == null ? null : actualNum - r.expected;
                const expired = !r.found && r.lot_assembled_at && r.lot_shelf && (() => {
                  const ex = new Date(r.lot_assembled_at + 'T00:00:00');
                  ex.setDate(ex.getDate() + r.lot_shelf);
                  const td = todayMSK();
                  return td >= ex;
                })();
                return (
                  <tr key={r.key} style={{ borderTop: '1px solid #EBEDEC',
                    background: diff !== null && diff !== 0 ? (diff > 0 ? '#EDFBEF' : '#FFF4F0') : '#fff' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 500 }}>
                      {r.bouquet_name}
                      {r.found && <span className="badge badge-info" style={{ marginLeft: 6 }}>найден</span>}
                      {r.found && (
                        <button type="button" className="btn-icon is-danger" title="Убрать" aria-label="Убрать"
                          onClick={() => removeRow(r.key)} style={{ marginLeft: 6, verticalAlign: 'middle' }}>
                          <TrashIcon />
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', color: '#8A8A85', fontSize: 12 }}>
                      {r.found
                        ? `сборка ${formatDate(r.found_assembled_at)}${r.found_shelf ? `, ${r.found_shelf} дн` : ''}`
                        : `#${r.lot_id} · ${formatDate(r.lot_assembled_at)}${r.lot_shelf ? `, ${r.lot_shelf} дн` : ''}`}
                      {expired && <span style={{ marginLeft: 6, background: '#FFE7DD', color: '#B0603C', '--ch': '5px', padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>истёк</span>}
                      {r.lot_gone && <span style={{ marginLeft: 6, background: '#FFE7DD', color: '#CB4D45', '--ch': '5px', padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>партии больше нет</span>}
                      {r.expected_changed && !r.lot_gone && <span style={{ marginLeft: 6, background: '#F9EBCF', color: '#7E5D00', '--ch': '5px', padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>система изменилась {r.expected_prev}→{r.expected}</span>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'center', color: '#999' }}>{r.expected}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <input type="text" inputMode="numeric" value={r.actual} placeholder="—"
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => updateRow(r.key, { actual: e.target.value.replace(/\D/g, '') })}
                        style={{ width: '100%', padding: '4px 6px', textAlign: 'center', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13, boxSizing: 'border-box' }} />
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600,
                      color: diff === null ? '#ccc' : diff === 0 ? '#999' : diff > 0 ? '#359250' : '#CB4D45' }}>
                      {diff === null ? '—' : diff === 0 ? '0' : (diff > 0 ? `+${diff}` : diff)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 16, padding: 12, background: '#F7F7F4', '--ch': '8px'}}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>+ Найденный букет (излишек)</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={foundBid} onChange={(e) => setFoundBid(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid #ddd', '--ch': '5px', '--stroke': '#ddd', minWidth: 180 }}>
            <option value="">Букет…</option>
            {allBouquets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <input type="number" min="1" placeholder="Кол-во" value={foundQty}
            onChange={(e) => setFoundQty(e.target.value)}
            style={{ width: 80, padding: '6px 8px', border: '1px solid #ddd', '--ch': '5px', '--stroke': '#ddd'}} />
          <input type="date" value={foundDate} onChange={(e) => setFoundDate(e.target.value)}
            title="Дата сборки" style={{ padding: '6px 8px', border: '1px solid #ddd', '--ch': '5px', '--stroke': '#ddd'}} />
          <input type="number" min="1" placeholder="Срок, дн" value={foundShelf}
            onChange={(e) => setFoundShelf(e.target.value)}
            style={{ width: 90, padding: '6px 8px', border: '1px solid #ddd', '--ch': '5px', '--stroke': '#ddd'}} />
          {/* Главная в окне одна — «Завершить»; эта — контурная (UX-аудит 25.10) */}
          <button type="button" className="btn btn-sm btn-secondary" onClick={addFound}>Добавить букет</button>
        </div>
      </div>
    </Modal>
    {completeConfirm && (
      <ConfirmDialog
        title="Завершить инвентаризацию букетов"
        message={completeConfirm.summary}
        confirmText="Завершить"
        onConfirm={() => doComplete(completeConfirm.rows)}
        onCancel={() => setCompleteConfirm(null)}
      />
    )}
    </>
  );
}

function BouquetInventoryViewModal({ inventory, onClose }) {
  return (
    <Modal title={`Инвентаризация букетов №${inventory.id} от ${docDateTimeText(inventory.date, inventory.completed_at || inventory.created_at)}`} size="xl" onClose={onClose}>
      <div style={{ display: 'flex', gap: 24, marginBottom: 16, fontSize: 14, flexWrap: 'wrap' }}>
        <div><strong>Статус:</strong> <span className="badge badge-active">Завершена</span></div>
        {inventory.completed_at && <div><strong>Завершена:</strong> {formatDateTime(inventory.completed_at)}</div>}
        {inventory.created_by_name && <div><strong>Провёл:</strong> {inventory.created_by_name}</div>}
      </div>
      {inventory.comment && (
        <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>{inventory.comment}</div>
      )}
      <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
        Позиций: {inventory.total_items} · Посчитано: {inventory.counted_items} · Расхождений: {inventory.total_differences}
      </div>
      <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'hidden' }}>
        <div style={{ maxHeight: 450, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9F9F6', position: 'sticky', top: 0, zIndex: 1 }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Букет</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Партия</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Система</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Факт</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, width: 90 }}>Разница</th>
              </tr>
            </thead>
            <tbody>
              {(inventory.items || []).map(it => {
                const diff = it.difference;
                return (
                  <tr key={it.id} style={{ borderTop: '1px solid #EBEDEC',
                    background: diff !== null && diff !== 0 ? (diff > 0 ? '#EDFBEF' : '#FFF4F0') : '#fff' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 500 }}>{it.bouquet_name}</td>
                    <td style={{ padding: '8px 10px', color: '#8A8A85', fontSize: 12 }}>
                      {it.lot_id ? `#${it.lot_id} · ${formatDate(it.lot_assembled_at)}` : 'найден'}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'center', color: '#999' }}>{it.expected_quantity}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 500 }}>{it.actual_quantity ?? '—'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600,
                      color: diff === null ? '#ccc' : diff === 0 ? '#999' : diff > 0 ? '#359250' : '#CB4D45' }}>
                      {diff === null ? '—' : diff === 0 ? '0' : (diff > 0 ? `+${diff}` : diff)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================================
// Единый реестр инвентаризаций (стебли + букеты в одном списке)
// ============================================================================
function InventoryRegistry({ showToast, stemsCreateTrigger, onStemsHandled, bouquetCreateTrigger, onBouquetHandled }) {
  const [stemEdit, setStemEdit] = useState(null);   // 'new' | объект черновика
  const [stemView, setStemView] = useState(null);
  const [bqEdit, setBqEdit] = useState(null);
  const [bqView, setBqView] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Стебли и букеты одной страницей с сервера (/warehouse/inventories/registry, у строк kind):
  // раньше оба списка тянулись целиком и сливались здесь — со страницами по 50 так нельзя.
  const fetchPage = useCallback(({ limit, offset }) => api.getInventoryRegistry({ limit, offset }), []);
  const onListError = useCallback(() => showToast('Ошибка загрузки инвентаризаций', 'error'), [showToast]);
  const { items: rows, isLoading, hasMore, loadingMore, reload: loadData, loadMore } =
    usePagedList(fetchPage, { onError: onListError });

  useEffect(() => { if (stemsCreateTrigger) { setStemEdit('new'); onStemsHandled && onStemsHandled(); } }, [stemsCreateTrigger, onStemsHandled]);
  useEffect(() => { if (bouquetCreateTrigger) { setBqEdit('new'); onBouquetHandled && onBouquetHandled(); } }, [bouquetCreateTrigger, onBouquetHandled]);

  const statusLabel = { draft: 'Черновик', completed: 'Завершена' };
  const statusBadge = { draft: 'badge-new', completed: 'badge-active' };

  const openRow = async (row) => {
    try {
      if (row.kind === 'stems') {
        const data = await api.getInventory(row.id);
        if (row.status === 'draft') setStemEdit(data); else setStemView(data);
      } else {
        const data = await api.getBouquetInventory(row.id);
        if (row.status === 'draft') setBqEdit(data); else setBqView(data);
      }
    } catch { showToast('Ошибка загрузки', 'error'); }
  };

  const handleDelete = (row) => {
    setConfirmDialog({
      title: 'Удаление инвентаризации', message: 'Удалить черновик инвентаризации?',
      confirmText: 'Удалить', variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          if (row.kind === 'stems') await api.deleteInventory(row.id);
          else await api.deleteBouquetInventory(row.id);
          showToast('Черновик удалён'); loadData();
        } catch (e) { showToast(e.message || 'Ошибка удаления', 'error'); }
      },
    });
  };

  return (
    <>
      {isLoading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="table-container" style={{ marginTop: 16 }}>
          <table className="table">
            <thead><tr>
              <th style={{ width: 60 }}>ID</th>
              <th style={{ width: 90 }}>Тип</th>
              <th>Дата</th><th>Статус</th><th>Позиций</th><th>Посчитано</th>
              <th>Расхождений</th><th>Провёл</th><th>Комментарий</th><th style={{ width: 40 }}></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="10" style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>Нет инвентаризаций</td></tr>
              ) : rows.map(row => (
                <tr key={`${row.kind}-${row.id}`} style={{ cursor: 'pointer' }} onClick={() => openRow(row)}>
                  <td>#{row.id}</td>
                  <td>
                    <span className={`badge ${row.kind === 'bouquets' ? 'badge-info' : 'badge-active'}`}>
                      {row.kind === 'bouquets' ? 'Букеты' : 'Стебли'}
                    </span>
                  </td>
                  <td><DocDateTime date={row.date} ts={row.completed_at || row.created_at} verb={row.completed_at ? 'проведена' : 'создана'} /></td>
                  <td><span className={`badge ${statusBadge[row.status]}`}>{statusLabel[row.status] || row.status}</span></td>
                  <td>{row.total_items}</td>
                  <td>{row.counted_items}</td>
                  <td>{row.total_differences}</td>
                  <td>{row.created_by_name || '—'}</td>
                  {/* Длинный комментарий — в одну строку с многоточием (полный — в подсказке): иначе
                      на узком экране строка вырастала в 4–5 раз. Ширину ограничивает div: max-width
                      у самой ячейки браузеры в обычной таблице не соблюдают */}
                  <td title={row.comment || ''}><div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.comment || '—'}</div></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {/* Удалить черновик — красная корзина, как в строках других таблиц (UX-аудит 25.10) */}
                    {row.status === 'draft' && (
                      <button type="button" className="btn-icon is-danger" title="Удалить черновик" aria-label="Удалить черновик"
                        onClick={() => handleDelete(row)}>
                        <TrashIcon />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} />
        </div>
      )}

      {stemEdit && (
        <InventoryEditModal
          inventory={stemEdit === 'new' ? undefined : stemEdit}
          onClose={() => setStemEdit(null)}
          onSaved={() => { setStemEdit(null); loadData(); }}
          showToast={showToast}
        />
      )}
      {stemView && <InventoryViewModal inventory={stemView} onClose={() => setStemView(null)} />}
      {bqEdit && (
        <BouquetInventoryEditModal
          inventory={bqEdit === 'new' ? null : bqEdit}
          onClose={() => setBqEdit(null)}
          onSaved={() => { setBqEdit(null); loadData(); }}
          showToast={showToast}
        />
      )}
      {bqView && <BouquetInventoryViewModal inventory={bqView} onClose={() => setBqView(null)} />}
      {confirmDialog && <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog(null)} />}
    </>
  );
}

export default WarehousePage;
